// client/src/pages/School_Admin/Academics/TimetableDesignerPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
// THE ONE SHEET BUILDER, shared with the generator's wall chart: landscape A4,
// amber breaks, and — through `deliver` — a hidden iframe for printing. A page
// that built its own PDF here printed a different-looking week from the same
// data, and `window.print()` printed the navigation, the tabs and the run
// history around a grid cut off at the sixth column.
import { buildPdf, deliver } from './TimetableMasterGrid';
// THE ONE BELL SCHEDULE. Generated from packages/core by tools/sync-shared.py and
// byte-identical to the copy the generator builds weeks on, so a timetable is
// drawn on the grid it was actually built for.
import { buildDaySlots } from '../../../utils/bellSchedule.generated';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AppLoader from '../../../components/AppLoader';
import { useTranslation } from 'react-i18next';

import {fetchJson} from '../../../utils/fetchJson';
import { onAcademicYearChanged } from '../../../utils/academicYearContext';

/**
 * THE TIMETABLE THAT WAS NEVER GIVEN A NAME.
 *
 * The picker is built from the names on file and a row with no name has none to
 * offer, so nine of this school's rows — a whole nur_pri class's week from
 * 2025/2026 — could not be reached from anywhere in the product. They are on
 * file, they are in the register's way, and nothing could open them.
 *
 * A NUL byte rather than a readable sentinel, because a school is perfectly
 * entitled to call a timetable "(unnamed)" and that one must not open somebody
 * else's rows. It is displayed as a label and never written back: saving asks
 * for a real name first.
 */
const UNNAMED = '\u0000unnamed';

const TimetableDesignerPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  /**
   * THE GRID'S OWN COLOURS, taken from the theme rather than from Tailwind.
   *
   * The timetable is a raw <table> and its cells were painted with fixed
   * utility classes — bg-gray-100, bg-yellow-50, bg-green-50, text-gray-800.
   * Those are light values with no idea a theme exists, so on the dark palette
   * every cell stayed a pale box while the body text around it turned
   * near-white: light ink on a light ground, which is a timetable that renders
   * perfectly and cannot be read.
   *
   * Semantic slots, not literal colours, so the grid follows all four palettes
   * and any fifth. `alpha` over the mark keeps a break amber and a conflict red
   * on a white page AND on a near-black one, because a wash composites onto
   * whatever is behind it.
   */
  // THE TIMETABLE IS THE ONE GRID THAT CANNOT SHRINK TO FIT.
  //
  // It has a fixed time column plus one column per day, and on a phone the time
  // column was 200px of a 360px screen — 55% spent on labels, before a single
  // day was visible. Filling a week meant scrolling right to find the column and
  // back to see which period it was.
  const isPhone = useMediaQuery('(max-width:600px)');

  const grid = useMemo(() => ({
    border: `1px solid ${theme.palette.divider}`,
    head: {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.16)
        : theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    timeCell: {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.10)
        : theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    breakCell: {
      backgroundColor: alpha(theme.palette.warning.main, 0.16),
      color: theme.palette.warning.main,
    },
    conflictCell: { backgroundColor: alpha(theme.palette.error.main, 0.16) },
    publishedCell: { backgroundColor: alpha(theme.palette.success.main, 0.16) },
    emptyCell: { backgroundColor: 'transparent', color: theme.palette.text.primary },
    strong: { color: theme.palette.text.primary },
    muted: { color: theme.palette.text.secondary },
  }), [theme]);

  const FREE_PERIOD_ACTIVITY = 'Free Period';

  const [timetables, setTimetables] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [phaseContext, setPhaseContext] = useState({ id: null, code: '' });
  const [contextKey, setContextKey] = useState('');
  // rooms not used
  const [sessionLabel, setSessionLabel] = useState('');
  const [defaultSessionId, setDefaultSessionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [academicYearLoading, setAcademicYearLoading] = useState(true);
  const [academicYearError, setAcademicYearError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    classId: '',
    subjectIds: [],
    teacherBySubject: {},
    dayOfWeek: '1',
    startTime: '',
    endTime: '',
    sessionId: '',
    timetableType: 'class',
    status: 'draft',
    sequenceNumber: '',
    timetableName: ''
  });
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedDay, setSelectedDay] = useState('all');
  const [weekView, setWeekView] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [conflicts, setConflicts] = useState([]);
  const [overrideConflict, setOverrideConflict] = useState(false);
  // Break configuration via period counts
  const [shortBreakConfig, setShortBreakConfig] = useState({ labelKey: 'schoolAdmin.timetableDesigner.breakLabels.short', periodsBefore: 3, durationMinutes: 15 });
  const [longBreakConfig, setLongBreakConfig] = useState({ labelKey: 'schoolAdmin.timetableDesigner.breakLabels.long', periodsBefore: 6, durationMinutes: 45 });
  const [savingBatch, setSavingBatch] = useState(false);
  // Axis settings
  const [periodMinutes, setPeriodMinutes] = useState(60);
  const [dayStart, setDayStart] = useState('08:00');
  const [dayEnd, setDayEnd] = useState('17:00');
  const [weekStart, setWeekStart] = useState('1');
  const [weekEnd, setWeekEnd] = useState('5');
  const [isFreePeriod, setIsFreePeriod] = useState(false);
  const [subjectDropdownOpen, setSubjectDropdownOpen] = useState(false);
  const [viewTimetableName, setViewTimetableName] = useState('');
  const [draftEntries, setDraftEntries] = useState([]);
  const [draftDirty, setDraftDirty] = useState(false);

  const isNurPriPhase = useMemo(() => {
    return String(phaseContext?.code || '').toLowerCase() === 'nur_pri';
  }, [phaseContext?.code]);

  const getClassTeacherIdFor = useCallback((classId) => {
    if (classId == null || String(classId) === '') return '';
    const cls = (classes || []).find((c) => String(c?.id) === String(classId));
    const tid = cls?.classMasterId;
    return tid == null || tid === '' ? '' : String(tid);
  }, [classes]);

  const effectiveFormClassId = useMemo(() => {
    const raw = selectedClass !== 'all' ? selectedClass : (formData?.classId || '');
    return raw == null ? '' : String(raw);
  }, [formData?.classId, selectedClass]);

  const nurPriClassTeacherId = useMemo(() => {
    if (!isNurPriPhase) return '';
    return getClassTeacherIdFor(effectiveFormClassId);
  }, [effectiveFormClassId, getClassTeacherIdFor, isNurPriPhase]);

  const classTeacherFallbackOption = useMemo(() => {
    if (!nurPriClassTeacherId) return null;
    const cls = (classes || []).find((c) => String(c?.id) === String(effectiveFormClassId));
    const fallbackLabel = cls?.classMaster?.name || cls?.classTeacherName || cls?.classMasterName || t('schoolAdmin.timetableDesigner.labels.classTeacher', { defaultValue: 'Class Teacher' });
    return { id: String(nurPriClassTeacherId), name: fallbackLabel };
  }, [classes, effectiveFormClassId, nurPriClassTeacherId, t]);

  const isNurPriPrimaryClass = useMemo(() => {
    if (!isNurPriPhase || !effectiveFormClassId) return false;
    const cls = (classes || []).find((c) => String(c?.id) === String(effectiveFormClassId));
    return String(cls?.level || '').toLowerCase() === 'primary';
  }, [isNurPriPhase, effectiveFormClassId, classes]);

  const activeSessionId = useMemo(() => {
    if (defaultSessionId) return String(defaultSessionId);
    if (formData.sessionId) return String(formData.sessionId);
    return '';
  }, [defaultSessionId, formData.sessionId]);

  useEffect(() => {
    if (!isNurPriPhase) return;
    if (!effectiveFormClassId) return;
    if (!nurPriClassTeacherId) return;

    const normalizedSubjects = Array.isArray(formData?.subjectIds)
      ? formData.subjectIds.map(String).filter((v) => v && v !== '__close__')
      : [];

    setFormData((prev) => {
      const prevMap = (prev && typeof prev.teacherBySubject === 'object' && prev.teacherBySubject) ? prev.teacherBySubject : {};
      const nextMap = {};
      for (const sid of normalizedSubjects) {
        if (isNurPriPrimaryClass) {
          // Primary classes: keep existing selection, default to class teacher if unset
          nextMap[String(sid)] = prevMap[String(sid)] || String(nurPriClassTeacherId);
        } else {
          // Nursery classes: always force class teacher
          nextMap[String(sid)] = String(nurPriClassTeacherId);
        }
      }
      const prevKeys = Object.keys(prevMap).sort();
      const nextKeys = Object.keys(nextMap).sort();
      if (prevKeys.length === nextKeys.length && nextKeys.every((k) => String(prevMap[k] || '') === String(nextMap[k] || ''))) {
        return prev;
      }
      return { ...prev, teacherBySubject: nextMap };
    });
  }, [isNurPriPhase, isNurPriPrimaryClass, effectiveFormClassId, nurPriClassTeacherId, formData?.subjectIds]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getRequestErrorMessage = (err, fallback = '') => {
    const backendMessage = err?.data?.userMessage || err?.data?.publicMessage || err?.data?.error || err?.data?.message;
    if (typeof backendMessage === 'string' && backendMessage.trim()) return backendMessage.trim();
    if (typeof err?.userMessage === 'string' && err.userMessage.trim()) return err.userMessage.trim();
    if (typeof err?.message === 'string' && err.message.trim()) return err.message.trim();
    return fallback;
  };

  const daysOfWeek = useMemo(() => ([
    { value: '1', label: t('schoolAdmin.timetableDesigner.days.monday') },
    { value: '2', label: t('schoolAdmin.timetableDesigner.days.tuesday') },
    { value: '3', label: t('schoolAdmin.timetableDesigner.days.wednesday') },
    { value: '4', label: t('schoolAdmin.timetableDesigner.days.thursday') },
    { value: '5', label: t('schoolAdmin.timetableDesigner.days.friday') },
    { value: '6', label: t('schoolAdmin.timetableDesigner.days.saturday') },
    { value: '7', label: t('schoolAdmin.timetableDesigner.days.sunday') }
  ]), [t]);

  // `toMin` went with the local bell schedule: the shared `buildDaySlots` does
  // its own arithmetic, and `safeToMinutes` below — which returns null rather
  // than 0 for something that is not a time — is what the rest of this page uses.
  const toHHMM = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
  };
  const safeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const parts = timeStr.split(':');
    if (parts.length < 2) return null;
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };
  /**
   * The day this grid is drawn on.
   *
   * ONE BELL SCHEDULE, SHARED. This page carried its own copy of the arithmetic
   * — periods, then a break after N of them, then the rest of the day, with the
   * afternoon shifted by however long the break ran. The generator has the same
   * arithmetic in `packages/core`, and `tools/sync-shared.py` keeps
   * `bellSchedule.generated.js` byte-identical to it. Two copies of a rule that
   * decides where every lesson is drawn is the whole class of bug these
   * generated files exist to end.
   *
   * The labels are put on here rather than inside the shared builder: a break is
   * called something different in French, and the arithmetic should not know
   * which language a school reads.
   */
  const displaySlots = useMemo(() => {
    const slots = buildDaySlots({
      dayStart,
      dayEnd,
      periodMinutes: Math.max(Number(periodMinutes) || 0, 5),
      shortBreak: shortBreakConfig,
      longBreak: longBreakConfig,
      weekStart: Number(weekStart) || 1,
      weekEnd: Number(weekEnd) || 5,
    });
    const labelFor = (slot, index) => {
      const source = index === 0 ? shortBreakConfig : longBreakConfig;
      if (source?.labelKey) return t(source.labelKey);
      return String(slot.label || source?.label || t('schoolAdmin.timetableDesigner.breakLabels.break'));
    };
    let seen = -1;
    return slots.map((slot) => {
      if (slot.type !== 'break') return slot;
      seen += 1;
      return { ...slot, label: labelFor(slot, seen) };
    });
  }, [dayStart, dayEnd, periodMinutes, shortBreakConfig, longBreakConfig, weekStart, weekEnd, t]);
  const periodSlots = useMemo(() => displaySlots.filter(slot => slot.type === 'period'), [displaySlots]);
  const computedSlots = useMemo(
    () => periodSlots.map(slot => `${slot.start}-${slot.end}`),
    [periodSlots]
  );
  const computedDays = useMemo(
    () => daysOfWeek.filter(d => Number(d.value) >= Number(weekStart) && Number(d.value) <= Number(weekEnd)),
    [daysOfWeek, weekStart, weekEnd]
  );

  const normTime = (t) => {
    if (!t) return '';
    if (t instanceof Date && !Number.isNaN(t.getTime())) {
      return `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
    }
    const s = String(t).trim();
    if (!s) return '';

    const minutes = safeToMinutes(s);
    if (minutes != null) return toHHMM(minutes);
    try {
      const m = s.match(/(\d{1,2}):(\d{2})/);
      if (m) {
        const hh = Math.max(0, Math.min(23, Number(m[1])));
        const mm = Math.max(0, Math.min(59, Number(m[2])));
        if (!Number.isNaN(hh) && !Number.isNaN(mm)) {
          return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
        }
      }
    } catch (_) {}
    try {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      }
    } catch (_) {}

    return s.length >= 5 ? s.slice(0, 5) : s;
  };

  /**
   * THE DAY THE SCHOOL ACTUALLY RUNS, not the day this browser last typed.
   *
   * The axis — start, close, period length, breaks, which days — used to live in
   * `localStorage` under `ttDesigner:axis:*`, keyed per browser. Three things
   * followed from that, and the third is why a school could not edit a generated
   * timetable at all:
   *
   *  · two admins saw different grids for the same class;
   *  · a new machine saw the built-in day and nothing said so;
   *  · a week applied by the generator had no entry at all, so the page fell
   *    back to 08:00–17:00 in 60-minute periods while the rows carried the
   *    school's real day of 07:30 in 50-minute ones. Every row landed between
   *    grid rows and the grid drew EMPTY. Nothing was wrong with the timetable;
   *    the ruler was wrong.
   *
   * It is read from the school now — the same `timetableConfigs` store the
   * generator writes, resolved by the same ladder: this class, then its phase,
   * then the school, then the built-in day. So the grid a week is drawn on is
   * the grid it was built for.
   */
  const [dayConfigs, setDayConfigs] = useState({ byClass: {}, default: null });

  // WHICH WAY THE SAME TIMETABLE IS BEING READ. 'class' is what this page has
  // always done; 'teacher' gathers the rows one person teaches from every class
  // they take.
  const [viewMode, setViewMode] = useState('class');
  const [selectedTeacherKey, setSelectedTeacherKey] = useState('');
  const [placeholderNames, setPlaceholderNames] = useState({});

  const loadDayConfigs = useCallback(async () => {
    try {
      const res = await fetchJson('/api/academics/timetables/day-config', { method: 'GET' });
      const data = res?.data || {};
      const byClass = {};
      for (const c of data.classes || []) byClass[String(c.classId)] = c;
      setDayConfigs({ byClass, default: data.default || null });
    } catch (_) {
      // Not fatal: the built-in day still draws a grid, and every other part of
      // this page works. Better a default grid than a blank screen.
    }
  }, []);

  /** The day config for whichever class is on screen, or the school's. */
  const activeDayConfig = useMemo(() => {
    const own = selectedClass && selectedClass !== 'all'
      ? dayConfigs.byClass[String(selectedClass)]
      : null;
    return own || dayConfigs.default || null;
  }, [dayConfigs, selectedClass]);

  // SEEDED, NOT MERGED. Whenever the class changes the axis becomes that class's
  // day; an admin may still nudge it for what they are looking at, and nothing
  // is written back — the school day is set on the generator's School day tab,
  // which is the one place that owns it.
  useEffect(() => {
    const cfg = activeDayConfig?.config;
    if (!cfg) return;
    if (cfg.dayStart) setDayStart(cfg.dayStart);
    if (cfg.dayEnd) setDayEnd(cfg.dayEnd);
    if (Number(cfg.periodMinutes) > 0) setPeriodMinutes(Math.max(5, Number(cfg.periodMinutes)));
    if (cfg.weekStart != null) setWeekStart(String(cfg.weekStart));
    if (cfg.weekEnd != null) setWeekEnd(String(cfg.weekEnd));
    // The label keys are kept: the shape comes from the school, the wording from
    // whichever language the reader has chosen.
    setShortBreakConfig((prev) => ({ ...prev, ...(cfg.shortBreak || {}) }));
    setLongBreakConfig((prev) => ({ ...prev, ...(cfg.longBreak || {}) }));
  }, [activeDayConfig]);

  useEffect(() => {
    fetchData();
    loadDayConfigs();
  }, [loadDayConfigs]);

  useEffect(() => {
    const unsub = onAcademicYearChanged(() => {
      fetchData();
      loadDayConfigs();
    });
    return unsub;
  }, []);

  useEffect(() => {
    const onPhase = () => {
      fetchData();
      loadDayConfigs();
    };
    window.addEventListener('app:phase:changed', onPhase);
    return () => window.removeEventListener('app:phase:changed', onPhase);
  }, []);

  useEffect(() => {
    setSelectedClass('all');
    setSelectedDay('all');
    setViewTimetableName('');
    setShowForm(false);
    setEditingEntry(null);
    setIsFreePeriod(false);
    setOverrideConflict(false);
    setFormData((prev) => ({
      ...prev,
      classId: '',
      subjectIds: [],
      teacherBySubject: {},
      dayOfWeek: '1',
      startTime: '',
      endTime: ''
    }));
  }, [phaseContext?.id, phaseContext?.code]);

  useEffect(() => {
    if (!activeSessionId) return;
    setFormData((prev) => {
      const prevSession = prev.sessionId ? String(prev.sessionId) : '';
      if (prevSession === String(activeSessionId)) return prev;
      return { ...prev, sessionId: String(activeSessionId) };
    });
  }, [activeSessionId]);

  // Helper: filter timetables by selected meta
  const matchesMeta = (t) => {
    if (!t) return false;
    const currentName = String(formData.timetableName || '').trim();
    // Never show entries without a timetable name set
    if (!currentName) return false;
    // Phase filter (extra safety net; backend is authoritative)
    if (phaseContext?.id) {
      if (t.phaseId == null) return false;
      if (String(t.phaseId) !== String(phaseContext.id)) return false;
    }
    if (activeSessionId && String(t.sessionId) !== String(activeSessionId)) return false;
    const tName = String(t.timetableName || '').trim();
    if (currentName === UNNAMED) {
      // The rows nobody named. Reachable on purpose — see UNNAMED.
      if (tName) return false;
    } else if (currentName) {
      if (!tName) return false;
      if (tName.toLowerCase() !== currentName.toLowerCase()) return false;
    }
    const tt = t.timetableType ?? t.type ?? 'class';
    if (formData.timetableType && tt !== formData.timetableType) return false;
    if (formData.timetableType === 'exam' && !formData.sequenceNumber) {
      return false;
    }
    if (formData.timetableType === 'exam' && formData.sequenceNumber) {
      const seq = t.sequenceNumber ?? t.seq ?? t.sequence;
      if (String(seq || '') !== String(formData.sequenceNumber)) return false;
    }
    return true;
  };

  const timetableNameOptions = useMemo(() => {
    const seen = new Map();
    let unnamed = 0;
    for (const t of timetables || []) {
      if (!t) continue;
      if (phaseContext?.id) {
        if (t.phaseId == null) continue;
        if (String(t.phaseId) !== String(phaseContext.id)) continue;
      }
      if (activeSessionId && String(t.sessionId) !== String(activeSessionId)) continue;
      const name = String(t.timetableName || '').trim();
      if (!name) { unnamed += 1; continue; }
      const key = name.toLowerCase();
      if (!seen.has(key)) seen.set(key, { value: name, label: name });
    }
    const arr = Array.from(seen.values());
    arr.sort((a, b) => a.label.localeCompare(b.label));
    // Last, because it is the odd one out, but present: a row nobody named is
    // still a row, and it was unreachable from every screen in the product.
    if (unnamed) {
      arr.push({
        value: UNNAMED,
        label: `${t('schoolAdmin.timetableDesigner.labels.unnamedTimetable', { defaultValue: '(unnamed)' })} · ${unnamed}`,
      });
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timetables, phaseContext, activeSessionId]);

  /**
   * ROWS THIS YEAR THAT CARRY NO PHASE, and so cannot be shown at all.
   *
   * The applier once wrote `phaseId: NULL` and 276 rows vanished from this page
   * with nothing said — the timetable was there, the page was empty, and there
   * was no way to tell that from having no timetable. Counted and reported, so
   * the next time it happens it is a sentence rather than a mystery.
   */
  const rowsWithoutPhase = useMemo(() => {
    if (!phaseContext?.id) return 0;
    return (timetables || []).filter((row) => row
      && row.phaseId == null
      && (!activeSessionId || String(row.sessionId) === String(activeSessionId))).length;
  }, [timetables, phaseContext, activeSessionId]);

  const currentTimetableName = useMemo(() => String(formData.timetableName || '').trim(), [formData.timetableName]);

  /** What to call the chosen timetable on screen and on paper. */
  const timetableLabel = useMemo(
    () => (currentTimetableName === UNNAMED
      ? t('schoolAdmin.timetableDesigner.labels.unnamedTimetable', { defaultValue: '(unnamed)' })
      : currentTimetableName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTimetableName],
  );
  const draftStorageKey = useMemo(() => {
    if (!contextKey) return '';
    if (!currentTimetableName) return '';
    if (selectedClass === 'all') return '';
    const seq = formData.timetableType === 'exam' ? String(formData.sequenceNumber || '') : '';
    return `ttDesigner:draft:${contextKey}:${selectedClass}:${formData.timetableType}:${seq}:${currentTimetableName}`;
  }, [contextKey, currentTimetableName, selectedClass, formData.timetableType, formData.sequenceNumber]);

  useEffect(() => {
    if (!draftStorageKey) {
      setDraftEntries([]);
      setDraftDirty(false);
      return;
    }
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setDraftEntries(parsed);
          setDraftDirty(parsed.length > 0);
          return;
        }
      }
    } catch (_) {}
    setDraftEntries([]);
    setDraftDirty(false);
  }, [draftStorageKey]);

  useEffect(() => {
    if (!draftStorageKey) return;
    try {
      if (draftDirty && Array.isArray(draftEntries) && draftEntries.length) {
        localStorage.setItem(draftStorageKey, JSON.stringify(draftEntries || []));
      } else {
        localStorage.removeItem(draftStorageKey);
      }
    } catch (_) {}
  }, [draftDirty, draftEntries, draftStorageKey]);

  const persistedEntriesForCurrent = useMemo(() => {
    if (!currentTimetableName) return [];
    if (selectedClass === 'all') return [];
    return (timetables || []).filter((t) => {
      if (!matchesMeta(t)) return false;
      if (String(t.classId) !== String(selectedClass)) return false;
      return true;
    });
  }, [timetables, currentTimetableName, selectedClass, activeSessionId, formData.timetableType, formData.sequenceNumber, phaseContext]);

  const workingEntries = useMemo(() => {
    if (!currentTimetableName) return [];
    if (selectedClass === 'all') return [];
    if (draftDirty && draftEntries && draftEntries.length) return draftEntries;
    return persistedEntriesForCurrent;
  }, [currentTimetableName, selectedClass, draftDirty, draftEntries, persistedEntriesForCurrent]);

  /**
   * ROWS UNDER ANOTHER NAME THAT SAVING WOULD REPLACE.
   *
   * The bulk save clears {school, class, session, type, +sequence} and
   * deliberately NOT the name — see the comment at timetableController.js:1005.
   * Scoping the delete by name is what this project already tried: it left
   * orphan rows behind from earlier names, and `resolveTimetableSlotForAttendance`
   * looks a slot up by class + subject + session with no name at all, so the
   * register saw the union of every timetable the class had ever had and marked
   * against whichever it found first.
   *
   * So: one timetable per class per year, a name is a label rather than a fork,
   * and the fix is to SAY what is about to go rather than to narrow the delete.
   */
  const rowsReplacedBySaving = useMemo(() => {
    if (selectedClass === 'all' || !currentTimetableName) return [];
    const type = formData.timetableType || 'class';
    const seq = type === 'exam' ? String(formData.sequenceNumber || '') : '';
    const counts = new Map();
    for (const row of timetables || []) {
      if (!row) continue;
      if (String(row.classId) !== String(selectedClass)) continue;
      if (activeSessionId && String(row.sessionId) !== String(activeSessionId)) continue;
      if ((row.timetableType ?? row.type ?? 'class') !== type) continue;
      if (type === 'exam' && String(row.sequenceNumber ?? '') !== seq) continue;
      const name = String(row.timetableName || '').trim();
      const isCurrent = currentTimetableName === UNNAMED
        ? !name
        : name.toLowerCase() === currentTimetableName.toLowerCase();
      if (isCurrent) continue;
      const label = name || t('schoolAdmin.timetableDesigner.labels.unnamedTimetable', { defaultValue: '(unnamed)' });
      counts.set(label, (counts.get(label) || 0) + 1);
    }
    return [...counts.entries()].map(([name, count]) => ({ name, count }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timetables, selectedClass, activeSessionId, currentTimetableName, formData.timetableType, formData.sequenceNumber]);

  /* -- reading the same timetable by teacher ------------------------------ */

  /**
   * WHO IS AGAINST A ROW — `u:93`, `p:4`, or null for a period nobody teaches.
   *
   * Never `Number(teacherId)`. `Number(null)` is 0, so keyed that way every
   * unstaffed period in the school and every placeholder collapse into one
   * phantom "Teacher 0" — the exact bug the generator's wall chart was written
   * to fix, and this school's week is 228 unstaffed periods, so it would have
   * been the biggest entry in the list.
   */
  const identityOf = useCallback((row) => {
    if (!row) return null;
    if (row.teacherId != null) return `u:${row.teacherId}`;
    if (row.placeholderTeacherId != null) return `p:${row.placeholderTeacherId}`;
    return null;
  }, []);

  /** Every row of the chosen timetable, whatever class it belongs to. */
  const entriesInTimetable = useMemo(
    () => (timetables || []).filter((t) => matchesMeta(t)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timetables, currentTimetableName, activeSessionId, formData.timetableType, formData.sequenceNumber, phaseContext],
  );

  /** The people teaching in it, with how much each carries. */
  const teachersInTimetable = useMemo(() => {
    const seen = new Map();
    for (const row of entriesInTimetable) {
      const key = identityOf(row);
      if (!key) continue;
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          name: key.startsWith('p:')
            ? (placeholderNames[key.slice(2)]
              || t('schoolAdmin.timetableDesigner.labels.noAccountTeacher', { defaultValue: 'Named, no account' }))
            : (row.Teacher?.name || getTeacherName(row.teacherId) || key),
          periods: 0,
        });
      }
      seen.get(key).periods += 1;
    }
    return [...seen.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entriesInTimetable, identityOf, placeholderNames, t]);

  // Pick somebody as soon as there is somebody to pick, so choosing "By teacher"
  // shows a week rather than an empty grid and a second thing to choose.
  useEffect(() => {
    if (viewMode !== 'teacher') return;
    if (selectedTeacherKey && teachersInTimetable.some((x) => x.key === selectedTeacherKey)) return;
    setSelectedTeacherKey(teachersInTimetable[0]?.key || '');
  }, [viewMode, teachersInTimetable, selectedTeacherKey]);

  const teacherEntries = useMemo(() => {
    if (viewMode !== 'teacher' || !selectedTeacherKey) return [];
    return entriesInTimetable.filter((row) => identityOf(row) === selectedTeacherKey);
  }, [viewMode, selectedTeacherKey, entriesInTimetable, identityOf]);

  /**
   * A teacher's columns are the union of the days of the classes they take.
   *
   * They move between classes, and this school's classes genuinely differ —
   * Forms 1 to 3 close at 15:00 in nine periods, the rest at 16:30 in ten. A
   * teacher drawn on any one class's grid loses every period that falls outside
   * it.
   */
  const teacherSlots = useMemo(() => {
    if (viewMode !== 'teacher') return null;
    const starts = new Map();
    for (const cid of new Set(teacherEntries.map((r) => String(r.classId)))) {
      const cfg = dayConfigs.byClass[cid];
      if (!cfg) continue;
      for (const s of cfg.daySlots || []) {
        const held = starts.get(s.start);
        // A break somewhere is only a break here if it is a break everywhere
        // that has it — one class teaching through another's break is a period.
        if (!held) starts.set(s.start, { ...s });
        else if (held.type === 'break' && s.type !== 'break') starts.set(s.start, { ...s });
      }
    }
    return [...starts.values()].sort((a, b) => String(a.start).localeCompare(String(b.start)));
  }, [viewMode, teacherEntries, dayConfigs]);

  /** The rows of the grid: this class's day, or the union of a teacher's. */
  const gridSlots = useMemo(
    () => (viewMode === 'teacher' && teacherSlots?.length ? teacherSlots : displaySlots),
    [viewMode, teacherSlots, displaySlots],
  );

  const ensureDraftInitialized = useCallback(() => {
    if (draftEntries && draftEntries.length) return;
    if (!persistedEntriesForCurrent || !persistedEntriesForCurrent.length) return;
    const normalized = persistedEntriesForCurrent.map((e) => ({
      ...e,
      dayOfWeek: String(e.dayOfWeek ?? e.day ?? ''),
      startTime: normTime(e.startTime),
      endTime: normTime(e.endTime),
      timetableName: String(e.timetableName || '').trim() || null,
      timetableType: e.timetableType ?? e.type ?? formData.timetableType ?? 'class',
      sequenceNumber: e.sequenceNumber ?? e.seq ?? e.sequence ?? null
    }));
    setDraftEntries(normalized);
    setDraftDirty(false);
  }, [draftEntries, persistedEntriesForCurrent, formData.timetableType]);

  const loadTimetableByName = (nameRaw) => {
    const name = String(nameRaw || '').trim();
    if (!name) return;
    const matches = (timetables || []).filter((t) => {
      if (!t) return false;
      if (phaseContext?.id) {
        if (t.phaseId == null) return false;
        if (String(t.phaseId) !== String(phaseContext.id)) return false;
      }
      if (activeSessionId && String(t.sessionId) !== String(activeSessionId)) return false;
      const tName = String(t.timetableName || '').trim();
      if (name === UNNAMED) return !tName;
      return tName && tName.toLowerCase() === name.toLowerCase();
    });
    if (!matches.length) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.noEntriesForTimetableName'), 'warning');
      return;
    }
    const first = matches[0];
    const resolvedType = first.timetableType ?? first.type ?? formData.timetableType ?? 'class';
    const seq = first.sequenceNumber ?? first.seq ?? first.sequence;
    setViewTimetableName(name);
    setFormData((prev) => ({
      ...prev,
      timetableName: name,
      timetableType: resolvedType,
      sequenceNumber: resolvedType === 'exam' ? String(seq ?? '') : ''
    }));
    if (first?.classId != null && String(first.classId)) {
      // No axis to restore: selecting the class seeds it from that class's day.
      setSelectedClass(String(first.classId));
      setFormData((prev) => ({ ...prev, classId: String(first.classId) }));
    }

    const normalized = matches.map((e) => ({
      ...e,
      dayOfWeek: String(e.dayOfWeek ?? e.day ?? ''),
      startTime: normTime(e.startTime),
      endTime: normTime(e.endTime),
      // The sentinel is a way to SELECT rows, never a name to give them. A draft
      // that carried it would put a NUL byte in the database the moment somebody
      // pressed Save.
      timetableName: name === UNNAMED ? null : name,
      timetableType: resolvedType,
      sequenceNumber: resolvedType === 'exam' ? (seq ?? null) : null
    }));
    setDraftEntries(normalized);
    setDraftDirty(false);
  };

  // Recompute view and conflicts when meta/draft changes
  useEffect(() => {
    generateWeekView(workingEntries);
    detectAndSetConflicts(workingEntries);
  }, [workingEntries, computedDays, computedSlots]);

  const normalizeGlobalYears = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.years)) return payload.years;
    if (Array.isArray(payload.data?.rows)) return payload.data.rows;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.globalAcademicYears)) return payload.globalAcademicYears;
    return [];
  };

  let sessionLabelValue = sessionLabel;
  
  const determineAndSetSessionLabel = useCallback((years) => {
    if (!years || years.length === 0) {
      console.warn('No academic years available');
      return;
    }
    
    // Find current year by isCurrent flag
    const currentYear = years.find(y => y.isCurrent) || years[0];
    
    // Robust label extraction
    const label = currentYear.name || currentYear.label || 
                currentYear.displayName || currentYear.title || 
                `${currentYear.startYear}/${currentYear.endYear}`;
                
    // Update session label value
    sessionLabelValue = label;
    
    // Fallback to ID if no name available
    if (!label) {
      console.warn('No name available for academic year, using ID:', currentYear.id);
      sessionLabelValue = t('schoolAdmin.timetableDesigner.session.academicYearWithId', { id: currentYear.id });
    }
    
    // Update state
    setSessionLabel(sessionLabelValue);
  }, [t]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setAcademicYearLoading(true);
      let maxGuard; try { maxGuard = setTimeout(() => setLoading(false), 2000); } catch(_) {}
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayIdRaw = localStorage.getItem(ayKey) || '';
      let normalizedAyId = '';
      let normalizedAyLabel = '';
      if (ayIdRaw) {
        try {
          const parsed = JSON.parse(ayIdRaw);
          if (parsed && typeof parsed === 'object') {
            const guessId = parsed.id ?? parsed.value ?? parsed.sessionId ?? parsed.academicYearId;
            if (guessId != null && guessId !== '') normalizedAyId = String(guessId);
            normalizedAyLabel = parsed.name || parsed.label || parsed.text || parsed.title || '';
          }
        } catch (_) {
          if (!Number.isNaN(parseInt(ayIdRaw, 10))) {
            normalizedAyId = String(parseInt(ayIdRaw, 10));
          }
        }
      }
      if (!normalizedAyId && ayIdRaw && !Number.isNaN(parseInt(ayIdRaw, 10))) {
        normalizedAyId = String(parseInt(ayIdRaw, 10));
      }
      const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${normalizedAyId || '-'}`;
      setContextKey(ctxKey);
      const normalizedPhaseId = phaseId && !Number.isNaN(parseInt(phaseId, 10))
        ? parseInt(phaseId, 10)
        : null;
      setPhaseContext({
        id: normalizedPhaseId,
        code: phaseCode || ''
      });

      // Prefill from cache to avoid flash
      try {
        const ttRaw = localStorage.getItem(`ttDesigner:timetables:${ctxKey}`);
        if (ttRaw) { const v = JSON.parse(ttRaw); if (Array.isArray(v)) setTimetables(v); }
      } catch(_) {}
      try { const raw = localStorage.getItem(`ttDesigner:classes:${ctxKey}`); if (raw) { const v = JSON.parse(raw); if (Array.isArray(v)) setClasses(v); } } catch(_) {}
      try { const raw = localStorage.getItem(`ttDesigner:subjects:${ctxKey}`); if (raw) { const v = JSON.parse(raw); if (Array.isArray(v)) setSubjects(v); } } catch(_) {}

      const settled = await Promise.allSettled([
        fetchJson('/api/academics/timetables', { method: 'GET' }),
        fetchJson('/api/classes', { method: 'GET' }),
        fetchJson('/api/subjects', { method: 'GET' }),
        fetchJson('/api/global-academic-years', { method: 'GET' }),
        fetchJson('/api/academics/timetables/teacher-list', { method: 'GET' }),
        fetchJson('/api/academics/timetables/teacher-subjects', { method: 'GET' }),
        fetchJson('/api/class-subjects/lite', { method: 'GET' })
      ]);

      const timetablesData = settled[0].status === 'fulfilled' ? settled[0].value : null;
      const classesData = settled[1].status === 'fulfilled' ? settled[1].value : null;
      const subjectsData = settled[2].status === 'fulfilled' ? settled[2].value : null;
      const globalYearsData = settled[3].status === 'fulfilled' ? settled[3].value : null;
      const teachersData = settled[4].status === 'fulfilled' ? settled[4].value : null;
      const teacherSubjectsData = settled[5].status === 'fulfilled' ? settled[5].value : null;

      if (settled[3].status === 'rejected') {
        setAcademicYearError(settled[3].reason);
      } else {
        setAcademicYearError(null);
      }
      if (settled[0].status === 'rejected') {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.loadTimetableEntriesFail'), 'error');
      }
      if (settled[1].status === 'rejected') {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.loadClassesFail'), 'error');
      }
      if (settled[2].status === 'rejected') {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.loadSubjectsFail'), 'error');
      }
      if (settled[4].status === 'rejected') {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.loadTeachersFail'), 'error');
      }
      if (settled[5].status === 'rejected') {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.loadTeacherSubjectsFail'), 'error');
      }

      const timetablesArr = timetablesData?.data || timetablesData?.timetables || timetablesData || [];
      // The people a generated week names but who have no account. Their rows
      // carry `placeholderTeacherId` and a null `teacherId` on purpose, so
      // without this a teacher's week lists them as nobody in particular — and
      // naming them was the entire point.
      const placeholderArr = Array.isArray(timetablesData?.placeholders) ? timetablesData.placeholders : [];
      const classesArr = classesData?.data || classesData?.classes || classesData || [];
      const subjectsArr = subjectsData?.data || subjectsData?.subjects || subjectsData || [];
      const teachersArr = teachersData?.data || teachersData?.teachers || teachersData || [];
      const teacherSubjectsArr = teacherSubjectsData?.data || teacherSubjectsData?.rows || teacherSubjectsData || [];
      const classSubjectsData = settled[6].status === 'fulfilled' ? settled[6].value : null;
      const classSubjectsArr = classSubjectsData?.data || classSubjectsData?.rows || classSubjectsData || [];
      const globalYearsArr = normalizeGlobalYears(globalYearsData);
      determineAndSetSessionLabel(globalYearsArr);
      const labelFromYears = (id) => {
        if (!id) return '';
        const match = globalYearsArr.find(y => String(y?.id) === String(id));
        return match?.name || match?.label || match?.displayName || match?.title || '';
      };
      const currentGlobalYear = globalYearsArr.find(y => y?.isCurrent);
      const storedSessionLabel = normalizedAyLabel || labelFromYears(normalizedAyId);

      setTimetables(timetablesArr);
      setPlaceholderNames(Object.fromEntries(placeholderArr.map((p) => [String(p.id), p.name])));
      setClasses(classesArr);
      setSubjects(subjectsArr);
      setTeachers(Array.isArray(teachersArr) ? teachersArr : []);
      setTeacherSubjects(Array.isArray(teacherSubjectsArr) ? teacherSubjectsArr : []);
      setClassSubjects(Array.isArray(classSubjectsArr) ? classSubjectsArr : []);
      let resolvedSessionId = null;
      let resolvedSessionLabel = '';
      if (currentGlobalYear) {
        resolvedSessionId = String(currentGlobalYear.id);
        resolvedSessionLabel = currentGlobalYear.name || currentGlobalYear.label || labelFromYears(currentGlobalYear.id) || t('schoolAdmin.timetableDesigner.session.currentAcademicYear');
      } else if (normalizedAyId) {
        resolvedSessionId = normalizedAyId;
        resolvedSessionLabel = storedSessionLabel || labelFromYears(normalizedAyId) || t('schoolAdmin.timetableDesigner.session.currentAcademicYear');
      }
      if (!resolvedSessionId) {
        const firstYear = globalYearsArr[0];
        if (firstYear?.id) {
          resolvedSessionId = String(firstYear.id);
          resolvedSessionLabel = firstYear.name || firstYear.label || storedSessionLabel || t('schoolAdmin.timetableDesigner.session.currentAcademicYear');
        }
      }
      const preferredSessionId = resolvedSessionId || '';
      const preferredLabel =
        labelFromYears(preferredSessionId) ||
        resolvedSessionLabel ||
        storedSessionLabel ||
        normalizedAyLabel ||
        t('schoolAdmin.timetableDesigner.session.currentAcademicYear');
      setSessionLabel(preferredLabel || t('schoolAdmin.timetableDesigner.session.currentAcademicYear'));
      setDefaultSessionId(preferredSessionId);
      if (preferredSessionId) {
        try {
          localStorage.setItem(ayKey, JSON.stringify({ id: preferredSessionId, name: preferredLabel }));
        } catch (_) {}
      }
      try { localStorage.setItem(`ttDesigner:timetables:${ctxKey}`, JSON.stringify(timetablesArr)); } catch(_) {}
      try { localStorage.setItem(`ttDesigner:classes:${ctxKey}`, JSON.stringify(classesArr)); } catch(_) {}
      try { localStorage.setItem(`ttDesigner:subjects:${ctxKey}`, JSON.stringify(subjectsArr)); } catch(_) {}

      setLoading(false);
      setAcademicYearLoading(false);
      try { clearTimeout(maxGuard); } catch(_) {}
    } catch (error) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.fetchDataFail'), 'error');
      setLoading(false);
      setAcademicYearError(error);
      setAcademicYearLoading(false);
    }
  };

  const mergePersistedTimetableEntries = useCallback((savedEntries = [], removedEntries = []) => {
    const normalizedSaved = (Array.isArray(savedEntries) ? savedEntries : [savedEntries]).filter(Boolean);
    const removedIds = new Set(
      (Array.isArray(removedEntries) ? removedEntries : [removedEntries])
        .map((entry) => String(entry?.id || ''))
        .filter(Boolean)
    );
    const savedIds = new Set(
      normalizedSaved
        .map((entry) => String(entry?.id || ''))
        .filter(Boolean)
    );

    setTimetables((prev) => {
      const base = (Array.isArray(prev) ? prev : []).filter((entry) => {
        const id = String(entry?.id || '');
        if (removedIds.has(id)) return false;
        if (savedIds.has(id)) return false;
        return true;
      });

      return [...base, ...normalizedSaved].sort((a, b) => {
        const classCompare = String(a?.classId || '').localeCompare(String(b?.classId || ''));
        if (classCompare !== 0) return classCompare;
        const dayCompare = String(a?.dayOfWeek ?? a?.day ?? '').localeCompare(String(b?.dayOfWeek ?? b?.day ?? ''));
        if (dayCompare !== 0) return dayCompare;
        return String(a?.startTime || '').localeCompare(String(b?.startTime || ''));
      });
    });
  }, []);

  const persistTimetableEntries = async (entriesToSave, options = {}) => {
    const { successMessage, draftFallbackMessage } = options;
    if (!metaReady) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.metaRequiredSaving'), 'warning');
      return false;
    }
    if (selectedClass === 'all') {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.selectClassToSave'), 'warning');
      return false;
    }
    if ((formData.timetableType || 'class') === 'exam' && !formData.sequenceNumber) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.selectExamSequenceBeforeSaving'), 'warning');
      return false;
    }
    if (!entriesToSave || entriesToSave.length === 0) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.noEntriesToSave'), 'warning');
      return false;
    }

    const nurPriTeacherId = isNurPriPhase ? getClassTeacherIdFor(selectedClass) : '';
    if (isNurPriPhase && !nurPriTeacherId) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.assignClassTeacherBeforeSavingNurPri'), 'warning');
      return false;
    }

    const payloadEntries = entriesToSave.map(entry => ({
      dayOfWeek: String(entry.dayOfWeek ?? entry.day ?? ''),
      startTime: entry.startTime,
      endTime: entry.endTime,
      subjectId: entry.subjectId,
      teacherId: entry.subjectId && isNurPriPhase ? parseInt(String(nurPriTeacherId), 10) : entry.teacherId,
      // WHO HAS NO ACCOUNT, CARRIED SEPARATELY. `teacherId` is a USER id that
      // attendance and evaluation compare against, so a named-but-accountless
      // teacher cannot go in it. Left out of the payload, opening a generated
      // week and pressing Save silently stripped the name off every lesson they
      // take — 17 of them here.
      placeholderTeacherId: entry.placeholderTeacherId ?? null,
      activityTitle: entry.activityTitle || null,
      status: entry.status || 'draft',
      timetableType: entry.timetableType || formData.timetableType || 'class',
      sequenceNumber: (entry.timetableType || formData.timetableType || 'class') === 'exam'
        ? (entry.sequenceNumber ?? formData.sequenceNumber ?? null)
        : null,
      timetableName: (entry.timetableName || formData.timetableName || '').trim() || null
    }));

    if (payloadEntries.some(e => !e.dayOfWeek || !e.startTime || !e.endTime || (!e.subjectId && !e.activityTitle))) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.invalidEntriesBeforeSaving'), 'warning');
      return false;
    }

    // A LESSON WITH NOBODY AGAINST IT IS SAVED, AND COUNTED.
    //
    // This used to refuse the save outright, which is the client-side twin of a
    // server guard already removed: the school asked for subjects it has no
    // teacher for to be placed anyway, so an applied week here is 228 vacancies
    // and 17 placeholder-taught periods out of 276. Opening it and pressing Save
    // was rejected on the first row. The count goes in the message instead —
    // nothing silent, nothing blocked.
    const vacancies = payloadEntries.filter(
      e => e.subjectId && !e.teacherId && !e.placeholderTeacherId).length;

    // Asked, not prevented, and never by narrowing the delete — see
    // rowsReplacedBySaving for why that is the wrong fix.
    if (rowsReplacedBySaving.length) {
      const list = rowsReplacedBySaving.map((x) => `${x.name} (${x.count})`).join(', ');
      const ok = window.confirm(t('schoolAdmin.timetableDesigner.snack.confirmReplaceOtherNames', {
        list,
        className: getClassName(selectedClass),
        defaultValue: `Saving replaces this class's whole week. ${list} will be removed, because a class has one timetable and a name is a label on it. Continue?`,
      }));
      if (!ok) return false;
    }

    try {
      setSavingBatch(true);
      const normalizedClassId = typeof selectedClass === 'string' ? parseInt(selectedClass, 10) : selectedClass;
      await fetchJson('/api/academics/timetables/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          classId: normalizedClassId,
          sessionId: activeSessionId || formData.sessionId,
          timetableType: formData.timetableType,
          sequenceNumber: formData.timetableType === 'exam' ? formData.sequenceNumber : null,
          timetableName: (formData.timetableName || '').trim() || null,
          entries: payloadEntries
        })
      });
      showSnackbar(
        vacancies > 0
          ? `${successMessage || t('schoolAdmin.timetableDesigner.snack.timetableSavedSuccess')} `
            + t('schoolAdmin.timetableDesigner.snack.savedWithVacancies', {
              n: vacancies,
              defaultValue: `${vacancies} period(s) have no teacher yet.`,
            })
          : (successMessage || t('schoolAdmin.timetableDesigner.snack.timetableSavedSuccess')),
        'success',
      );
      setDraftDirty(false);
      setDraftEntries([]);
      try { if (draftStorageKey) localStorage.removeItem(draftStorageKey); } catch (_) {}
      fetchData();
      return true;
    } catch (error) {
      console.error('Failed to save timetable', error);
      const baseMessage = getRequestErrorMessage(error, t('schoolAdmin.timetableDesigner.snack.saveTimetableFail'));
      const message = draftFallbackMessage ? `${baseMessage} ${draftFallbackMessage}`.trim() : baseMessage;
      showSnackbar(message, 'error');
      return false;
    } finally {
      setSavingBatch(false);
    }
  };

  const persistSingleTimetableEntries = async (entriesToSave, existingEntries = [], options = {}) => {
    const { successMessage, draftFallbackMessage } = options;
    if (!metaReady) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.metaRequiredSaving'), 'warning');
      return false;
    }
    if (selectedClass === 'all') {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.selectClassToSave'), 'warning');
      return false;
    }
    if ((formData.timetableType || 'class') === 'exam' && !formData.sequenceNumber) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.selectExamSequenceBeforeSaving'), 'warning');
      return false;
    }
    if (!entriesToSave || entriesToSave.length === 0) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.noEntriesToSave'), 'warning');
      return false;
    }

    const nurPriTeacherId = isNurPriPhase ? getClassTeacherIdFor(selectedClass) : '';
    if (isNurPriPhase && !nurPriTeacherId) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.assignClassTeacherBeforeSavingNurPri'), 'warning');
      return false;
    }

    const normalizedClassId = typeof selectedClass === 'string' ? parseInt(selectedClass, 10) : selectedClass;
    const payloadEntries = entriesToSave.map((entry) => ({
      classId: normalizedClassId,
      dayOfWeek: String(entry.dayOfWeek ?? entry.day ?? ''),
      startTime: entry.startTime,
      endTime: entry.endTime,
      subjectId: entry.subjectId,
      teacherId: entry.subjectId && isNurPriPhase ? parseInt(String(nurPriTeacherId), 10) : entry.teacherId,
      // Carried, for the same reason as the bulk path above.
      placeholderTeacherId: entry.placeholderTeacherId ?? null,
      activityTitle: entry.activityTitle || null,
      status: entry.status || 'draft',
      timetableType: entry.timetableType || formData.timetableType || 'class',
      sequenceNumber: (entry.timetableType || formData.timetableType || 'class') === 'exam'
        ? (entry.sequenceNumber ?? formData.sequenceNumber ?? null)
        : null,
      timetableName: (entry.timetableName || formData.timetableName || '').trim() || null,
      sessionId: activeSessionId || formData.sessionId
    }));

    if (payloadEntries.some((e) => !e.dayOfWeek || !e.startTime || !e.endTime || (!e.subjectId && !e.activityTitle))) {
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.invalidEntriesBeforeSaving'), 'warning');
      return false;
    }

    try {
      setSavingBatch(true);
      const remainingExisting = Array.isArray(existingEntries) ? [...existingEntries] : [];
      const savedEntries = [];
      const takeExistingEntry = (payload) => {
        const sameKindIndex = remainingExisting.findIndex((row) => {
          if (!row?.id) return false;
          if (payload.activityTitle) return !!row?.activityTitle;
          return !row?.activityTitle && String(row?.subjectId ?? '') === String(payload.subjectId ?? '');
        });
        if (sameKindIndex >= 0) return remainingExisting.splice(sameKindIndex, 1)[0];
        const fallbackIndex = remainingExisting.findIndex((row) => !!row?.id);
        if (fallbackIndex >= 0) return remainingExisting.splice(fallbackIndex, 1)[0];
        return null;
      };

      for (const payload of payloadEntries) {
        const existing = takeExistingEntry(payload);
        if (existing?.id) {
          const response = await fetchJson(`/api/academics/timetables/${existing.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const saved = response?.data || response;
          if (saved) savedEntries.push(saved);
        } else {
          const response = await fetchJson('/api/academics/timetables', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const saved = response?.data || response;
          if (saved) savedEntries.push(saved);
        }
      }

      for (const leftover of remainingExisting) {
        if (!leftover?.id) continue;
        await fetchJson(`/api/academics/timetables/${leftover.id}`, {
          method: 'DELETE'
        });
      }

      showSnackbar(successMessage || t('schoolAdmin.timetableDesigner.snack.timetableSavedSuccess'), 'success');
      setDraftDirty(false);
      setDraftEntries([]);
      try { if (draftStorageKey) localStorage.removeItem(draftStorageKey); } catch (_) {}
      mergePersistedTimetableEntries(savedEntries, existingEntries);
      return true;
    } catch (error) {
      console.error('Failed to save timetable entry directly', error);
      const baseMessage = getRequestErrorMessage(error, t('schoolAdmin.timetableDesigner.snack.saveEntryFail'));
      const message = draftFallbackMessage ? `${baseMessage} ${draftFallbackMessage}`.trim() : baseMessage;
      showSnackbar(message, 'error');
      return false;
    } finally {
      setSavingBatch(false);
    }
  };

  // Detect overlaps across teacher, class
  const isOverlap = (a, b) => {
    if (!a || !b) return false;
    const da = a.dayOfWeek ?? a.day;
    const db = b.dayOfWeek ?? b.day;
    if (da != db) return false; // loose eq for string/number
    // times are HH:mm
    const toMin = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const aStart = toMin(a.startTime);
    const aEnd = toMin(a.endTime);
    const bStart = toMin(b.startTime);
    const bEnd = toMin(b.endTime);
    return Math.max(aStart, bStart) < Math.min(aEnd, bEnd);
  };

  const detectConflictsFor = (entry, all, ignoreKey = null) => {
    const issues = [];
    const ignore = ignoreKey != null && ignoreKey !== '' ? String(ignoreKey) : null;
    // helper to count overlapping class entries
    const classOverlapCount = all.filter(o => {
      if (!o) return false;
      if (ignore && String(o.id ?? o.__localId ?? '') === ignore) return false;
      return String(o.classId) === String(entry.classId) && isOverlap(entry, o);
    }).length;
    for (const other of all) {
      if (!other) continue;
      if (ignore && String(other.id ?? other.__localId ?? '') === ignore) continue;
      if (!isOverlap(entry, other)) continue;
      // Block class-time when an activity exists (like breaks)
      if ((other.activityTitle || entry.activityTitle) && String(other.classId) === String(entry.classId)) {
        issues.push(
          t('schoolAdmin.timetableDesigner.conflicts.activityBlock', {
            title: other.activityTitle || entry.activityTitle,
            start: other.startTime,
            end: other.endTime,
            className: getClassName(other.classId)
          })
        );
        continue;
      }
      // Allow multiple simultaneous subjects for same class/time
      // if (entry.classId && other.classId && entry.classId === other.classId && classOverlapCount >= 2) {
      //   issues.push(`Class conflict: more than 2 subjects at ${other.startTime}-${other.endTime} for ${getClassName(other.classId)}`);
      // }
    }
    return issues;
  };

  const detectAndSetConflicts = (all) => {
    const list = [];
    for (const e of all) {
      const issues = detectConflictsFor(e, all, e.id ?? e.__localId);
      if (issues.length) list.push({ id: e.id, issues });
    }
    setConflicts(list);
  };

  // Propose alternative slots to avoid clashes
  const proposeSlots = (entry) => {
    const suggestions = [];
    for (const day of computedDays) {
      for (const slot of computedSlots) {
        const [s, e] = slot.split('-');
        const trial = { ...entry, dayOfWeek: day.value, startTime: s, endTime: e };
        // Skip if activity already blocks this class/time
        const activityBlock = workingEntries.some(o => o && o.classId === trial.classId && (o.dayOfWeek ?? o.day) == trial.dayOfWeek && normTime(o.startTime) === normTime(trial.startTime) && o.activityTitle);
        if (activityBlock) continue;
        // Enforce max 2 subjects in class at same time
        // const classAtTime = timetables.filter(o => o && matchesMeta(o) && o.classId === trial.classId && (o.dayOfWeek ?? o.day) == trial.dayOfWeek && o.startTime === trial.startTime);
        // if (classAtTime.length >= 2) continue;
        suggestions.push({ dayLabel: day.label, slot });
        if (suggestions.length >= 6) return suggestions;
      }
    }
    return suggestions;
  };

  const generateWeekView = (timetables) => {
    const view = computedDays.map(day => ({
      day: day.label,
      slots: computedSlots.map(slot => {
        const [startTime] = slot.split('-');
        const entry = timetables.find(t => {
          const d = t.dayOfWeek ?? t.day;
          return d == day.value && normTime(t.startTime) === normTime(startTime); // loose eq for string/number
        });
        return {
          time: slot,
          entry: entry || null
        };
      })
    }));
    setWeekView(view);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let shouldCloseForm = false;
    try {
      if (!metaReady) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.metaRequiredAddEntries'), 'warning');
        return;
      }
      ensureDraftInitialized();
      // Basic period validation
      const toMin = (t) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + m;
      };
      if (!formData.startTime || !formData.endTime) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.selectStartEnd'), 'warning');
        return;
      }
      if (toMin(formData.endTime) <= toMin(formData.startTime)) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.endAfterStart'), 'warning');
        return;
      }
      // Exam-specific validation
      if (formData.timetableType === 'exam' && !formData.sequenceNumber) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.selectExamSequenceForExamTimetables'), 'warning');
        return;
      }
      const driveSlotDropdown = () => computedSlots.some(ts => {
        const [s, e] = ts.split('-');
        return s === formData.startTime && e === formData.endTime;
      });
      if (!driveSlotDropdown()) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.periodNotMatchSlots'), 'warning');
        return;
      }
      const toId = (val) => {
        if (val == null || val === '') return null;
        const n = parseInt(String(val), 10);
        return Number.isNaN(n) ? String(val) : n;
      };

      const selectedSubjectIds = Array.isArray(formData.subjectIds)
        ? formData.subjectIds.map(String).filter((v) => v && v !== '__close__')
        : [];

      const rawTeacherBySubject = (formData && typeof formData.teacherBySubject === 'object' && formData.teacherBySubject)
        ? formData.teacherBySubject
        : {};

      const classId = selectedClass !== 'all' ? selectedClass : formData.classId;
      const dayOfWeek = String(formData.dayOfWeek);
      const slotStart = normTime(formData.startTime);
      const slotEnd = normTime(formData.endTime);

      let teacherBySubject = rawTeacherBySubject;
      if (!isFreePeriod && isNurPriPhase) {
        const classTeacherId = getClassTeacherIdFor(classId);
        if (!classTeacherId) {
          showSnackbar(t('schoolAdmin.timetableDesigner.snack.assignClassTeacherBeforeNurPri'), 'warning');
          return;
        }
        if (!isNurPriPrimaryClass) {
          // Nursery: always force class teacher for every subject
          const next = {};
          for (const sid of selectedSubjectIds) {
            next[String(sid)] = String(classTeacherId);
          }
          teacherBySubject = next;
        } else {
          // Primary: default to class teacher if no teacher selected for a subject
          const next = { ...rawTeacherBySubject };
          for (const sid of selectedSubjectIds) {
            if (!next[String(sid)]) next[String(sid)] = String(classTeacherId);
          }
          teacherBySubject = next;
        }
      }

      const existingPool = (draftDirty && draftEntries && draftEntries.length) ? draftEntries : persistedEntriesForCurrent;
      const existingAtSlot = (existingPool || []).filter(t => {
        if (!t) return false;
        if (String(t.classId) !== String(classId)) return false;
        const d = t.dayOfWeek ?? t.day;
        return String(d) === String(dayOfWeek) && normTime(t.startTime) === normTime(slotStart);
      });

      const isEditingThisSlot = (() => {
        if (!editingEntry) return false;
        const eClassId = editingEntry?.classId;
        const eDay = editingEntry?.dayOfWeek ?? editingEntry?.day;
        return (
          String(eClassId) === String(classId)
          && String(eDay) === String(dayOfWeek)
          && normTime(editingEntry?.startTime) === normTime(slotStart)
        );
      })();

      if (isFreePeriod) {
        if (selectedSubjectIds.length) {
          showSnackbar(t('schoolAdmin.timetableDesigner.snack.freePeriodNoSubjects'), 'warning');
          return;
        }
        if (existingAtSlot.length && !overrideConflict && !isEditingThisSlot) {
          showSnackbar(t('schoolAdmin.timetableDesigner.snack.periodHasSubjectsOverrideFree'), 'warning');
          return;
        }
      } else {
        if (!selectedSubjectIds.length) {
          showSnackbar(t('schoolAdmin.timetableDesigner.snack.selectSubjectOrFree'), 'warning');
          return;
        }

        if (!isNurPriPhase || isNurPriPrimaryClass) {
          // NOBODY YET IS AN ANSWER. It used to be a refusal, which is why a
          // generated week could not be edited: the school asked for subjects it
          // has no teacher for to be placed anyway, and 228 of this week's
          // periods are exactly that.
          const identities = selectedSubjectIds
            .map((sid) => String(teacherBySubject[String(sid)] || ''))
            .filter(Boolean);
          // Blanks skipped: two unfilled periods are not the same person twice.
          if (!isNurPriPhase && new Set(identities).size !== identities.length) {
            showSnackbar(t('schoolAdmin.timetableDesigner.snack.eachSubjectDifferentTeacher'), 'warning');
            return;
          }
        }

        const blockedByFree = existingAtSlot.some(t => t.activityTitle);
        if (blockedByFree && !overrideConflict && !isEditingThisSlot) {
          showSnackbar(t('schoolAdmin.timetableDesigner.snack.periodMarkedFreeOverrideAddSubjects'), 'warning');
          return;
        }
      }

      const baseDraft = {
        classId: toId(classId),
        dayOfWeek,
        startTime: slotStart,
        endTime: slotEnd,
        sessionId: activeSessionId || formData.sessionId || null,
        status: formData.status || 'draft',
        timetableType: formData.timetableType || 'class',
        sequenceNumber: (formData.timetableType === 'exam') ? toId(formData.sequenceNumber) : null,
        timetableName: (formData.timetableName || '').trim() || null,
        teacherId: null,
        placeholderTeacherId: null
      };

      const keptEntries = (existingPool || []).filter((t) => {
        if (!t) return false;
        if (String(t.classId) !== String(baseDraft.classId)) return true;
        const d = t.dayOfWeek ?? t.day;
        if (String(d) !== String(baseDraft.dayOfWeek)) return true;
        if (normTime(t.startTime) !== normTime(baseDraft.startTime)) return true;
        return false;
      });

      const nextEntries = [];
      if (isFreePeriod) {
        nextEntries.push({ ...baseDraft, subjectId: null, activityTitle: FREE_PERIOD_ACTIVITY, __localId: `${Date.now()}-free` });
      } else {
        for (const sid of selectedSubjectIds) {
          const identity = String(teacherBySubject[String(sid)] || '');
          const { teacherId, placeholderTeacherId } = parseTeacherIdentity(identity);
          nextEntries.push({
            ...baseDraft,
            subjectId: toId(sid),
            teacherId: teacherId ?? null,
            placeholderTeacherId: placeholderTeacherId ?? null,
            activityTitle: null,
            __localId: `${Date.now()}-${sid}-${identity || 'vacant'}`,
          });
        }
      }

      const persistedAtSlot = (persistedEntriesForCurrent || []).filter((t) => {
        if (!t) return false;
        if (String(t.classId) !== String(baseDraft.classId)) return false;
        const d = t.dayOfWeek ?? t.day;
        if (String(d) !== String(baseDraft.dayOfWeek)) return false;
        return normTime(t.startTime) === normTime(baseDraft.startTime);
      });

      const hasDraftOutsideCurrentSlot = !!(draftDirty && Array.isArray(draftEntries) && draftEntries.some((t) => {
        if (!t) return false;
        if (String(t.classId) !== String(baseDraft.classId)) return true;
        const d = t.dayOfWeek ?? t.day;
        if (String(d) !== String(baseDraft.dayOfWeek)) return true;
        return normTime(t.startTime) !== normTime(baseDraft.startTime);
      }));

      const nextDraftEntries = [...keptEntries, ...nextEntries];
      shouldCloseForm = true;

      const canPersistDirectly = !hasDraftOutsideCurrentSlot && nextEntries.length === 1 && persistedAtSlot.length <= 1;

      if (canPersistDirectly) {
        const saved = await persistSingleTimetableEntries(nextEntries, persistedAtSlot, {
          successMessage: 'Entry saved and timetable updated successfully.',
          draftFallbackMessage: 'The entry remains in draft. Click Save Timetable to retry.'
        });
        if (!saved) {
          setDraftEntries(nextDraftEntries);
          setDraftDirty(true);
        }
      } else {
        setDraftEntries(nextDraftEntries);
        setDraftDirty(true);

        await persistTimetableEntries(nextDraftEntries, {
          successMessage: 'Entry saved and timetable updated successfully.',
          draftFallbackMessage: 'The entry remains in draft. Click Save Timetable to retry.'
        });
      }
    } catch (error) {
      console.error('Failed to save timetable entry', error);
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.saveEntryFail'), 'error');
    } finally {
      if (shouldCloseForm) {
        resetEntryForm();
      }
    }
  };

  const resetEntryForm = () => {
    setFormData((prev) => ({
      ...prev,
      classId: selectedClass !== 'all' ? selectedClass : (prev.classId || ''),
      subjectIds: [],
      teacherBySubject: {},
      dayOfWeek: '1',
      startTime: '',
      endTime: '',
      status: 'draft'
    }));
    setEditingEntry(null);
    setIsFreePeriod(false);
    setOverrideConflict(false);
    setShowForm(false);
    teacherTouchedRef.current = new Set();
  };

  /**
   * WHAT THE TEACHER PICKER HOLDS — `u:93`, `p:4`, or '' for nobody yet.
   *
   * The same three states a row has, because the picker is how a row gets one.
   * It used to hold a bare user id and offer only accounts, so a slot taught by
   * somebody the school named but never gave an account came up empty and the
   * dialog refused to save it; a vacancy could not be expressed at all.
   *
   * A bare number is read as a user id, so drafts saved by the old page still
   * open.
   */
  /** Subjects whose teacher the reader has set by hand in this dialog. */
  const teacherTouchedRef = useRef(new Set());

  const parseTeacherIdentity = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return { teacherId: null, placeholderTeacherId: null };
    if (raw.startsWith('p:')) return { teacherId: null, placeholderTeacherId: Number(raw.slice(2)) };
    if (raw.startsWith('u:')) return { teacherId: Number(raw.slice(2)), placeholderTeacherId: null };
    return { teacherId: Number(raw), placeholderTeacherId: null };
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    if (entry?.subjectId != null) teacherTouchedRef.current = new Set([String(entry.subjectId)]);
    // Normalize legacy fields
    const normalized = {
      ...entry,
      timetableType: entry.timetableType ?? entry.type ?? 'class',
      sequenceNumber: entry.sequenceNumber ?? entry.seq ?? '',
      status: entry.status ?? 'draft',
      dayOfWeek: entry.dayOfWeek ?? entry.day ?? '1'
    };
    setFormData((prev) => ({
      ...prev,
      ...normalized,
      timetableName: prev.timetableName,
      subjectIds: normalized?.subjectId ? [String(normalized.subjectId)] : [],
      teacherBySubject: normalized?.subjectId
        ? { [String(normalized.subjectId)]: identityOf(normalized) || '' }
        : {},
      sessionId: prev.sessionId || (normalized.sessionId ? String(normalized.sessionId) : '')
    }));
    setIsFreePeriod(!!entry.activityTitle);
    setShowForm(true);
  };

  const handleEditSlot = (entries, slotStart, slotEnd, dayValue) => {
    const list = Array.isArray(entries) ? entries : [];
    if (!list.length) return;
    const activity = list.find(e => (e?.activityTitle || '').trim());
    const subjectIds = list
      .filter(e => !e?.activityTitle)
      .map(e => e?.subjectId)
      .filter((v) => v != null && v !== '')
      .map((v) => String(v));
    const teacherBySubject = {};
    for (const e of list) {
      if (e?.activityTitle) continue;
      const sid = e?.subjectId;
      if (sid == null || sid === '') continue;
      // Whoever is against it — an account, a name with no account, or nobody.
      teacherBySubject[String(sid)] = identityOf(e) || '';
    }
    const base = list[0];
    setEditingEntry(base);
    setIsFreePeriod(!!activity);
    // What the row already says is a decision, not a blank awaiting a default —
    // including a vacancy, which is a decision the generator made on purpose.
    teacherTouchedRef.current = new Set(Object.keys(teacherBySubject));
    setFormData((prev) => ({
      ...prev,
      classId: selectedClass !== 'all' ? selectedClass : (base?.classId ?? prev.classId),
      dayOfWeek: String(dayValue ?? (base?.dayOfWeek ?? base?.day ?? prev.dayOfWeek ?? '1')),
      startTime: normTime(slotStart ?? base?.startTime ?? prev.startTime),
      endTime: normTime(slotEnd ?? base?.endTime ?? prev.endTime),
      subjectIds: activity ? [] : Array.from(new Set(subjectIds)),
      teacherBySubject: activity ? {} : teacherBySubject
    }));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      if (!metaReady) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.metaRequiredDeleteEntries'), 'warning');
        return;
      }
      ensureDraftInitialized();
      const key = String(id ?? '');
      setDraftEntries((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        return arr.filter((e) => String(e?.id ?? e?.__localId ?? '') !== key);
      });
      setDraftDirty(true);
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.entryRemovedDraft'), 'success');
    } catch (error) {
      console.error('Failed to delete timetable entry', error);
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.deleteEntryFail'), 'error');
    }
  };

  // Publishing workflows
  const updateStatus = async (id, status) => {
    try {
      if (!metaReady) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.metaRequiredUpdateEntries'), 'warning');
        return;
      }
      ensureDraftInitialized();
      const key = String(id ?? '');
      setDraftEntries((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        return arr.map((e) => {
          if (String(e?.id ?? e?.__localId ?? '') !== key) return e;
          return { ...e, status };
        });
      });
      setDraftDirty(true);
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.entryUpdatedDraft'), 'success');
    } catch (e) {
      console.error('Failed to update status', e);
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.updateStatusFail'), 'error');
    }
  };

  const publishScope = async (scope) => {
    try {
      if (draftDirty) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.saveTimetableFirstDraft'), 'warning');
        return;
      }
      if (!metaReady) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.metaRequiredPublishing'), 'warning');
        return;
      }
      await fetchJson(`/api/academics/timetables/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...scope,
          timetableType: formData.timetableType,
          sequenceNumber: formData.timetableType === 'exam' ? formData.sequenceNumber : null,
          timetableName: (formData.timetableName || '').trim() || null
        })
      });
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.publishedSuccess'), 'success');
    } catch (e) {
      console.error('Failed to publish to portals', e);
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.publishFail'), 'error');
    }
  };

  const feedExamDates = async (scope) => {
    try {
      if (draftDirty) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.saveTimetableFirstDraft'), 'warning');
        return;
      }
      await fetchJson(`/api/academics/exams/from-timetable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scope)
      });
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.examDatesFedSuccess'), 'success');
    } catch (e) {
      console.error('Failed to feed exam dates', e);
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.examDatesFedFail'), 'error');
    }
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => String(c.id) === String(classId));
    return cls ? cls.name : t('common.na');
  };

  const getSubjectName = (subjectId) => {
    if (subjectId == null || subjectId === '') return '';
    const subject = subjects.find(s => String(s.id) === String(subjectId));
    return subject ? subject.name : t('common.na');
  };

  const getTeacherName = (teacherId) => {
    if (teacherId == null || teacherId === '') return '';
    const teacher = teachers.find(t => String(t.id) === String(teacherId));
    if (teacher) return teacher.name || teacher.email || t('common.na');
    if (classTeacherFallbackOption && String(classTeacherFallbackOption.id) === String(teacherId)) {
      return classTeacherFallbackOption.name || t('schoolAdmin.timetableDesigner.labels.classTeacher', { defaultValue: 'Class Teacher' });
    }
    return t('common.na');
  };

  /**
   * WHO TEACHES A ROW — including the people who have no account.
   *
   * `getTeacherName` takes a USER id, and a placeholder has none: the row carries
   * `placeholderTeacherId` with `teacherId` deliberately null, because
   * `teacherId` is compared against real users by attendance, messaging and
   * evaluation. Asked for the user id alone it returns '' and the cell shows
   * nobody against a lesson the school took the trouble to name somebody for —
   * 17 such periods in this week. Empty stays empty for a genuine vacancy.
   */
  const rowTeacherName = (row) => {
    if (row?.teacherId != null) return getTeacherName(row.teacherId);
    if (row?.placeholderTeacherId != null) {
      return placeholderNames[String(row.placeholderTeacherId)]
        || t('schoolAdmin.timetableDesigner.labels.noAccountTeacher', { defaultValue: 'Named, no account' });
    }
    return '';
  };

  const allowedTeacherIdsBySubjectId = useMemo(() => {
    if (isNurPriPhase) {
      const tid = nurPriClassTeacherId;
      const out = {};
      const arr = Array.isArray(teacherSubjects) ? teacherSubjects : [];
      for (const s of subjects || []) {
        if (!s?.id) continue;
        const sid = String(s.id);
        if (isNurPriPrimaryClass) {
          // Primary: class teacher (default) + any teachers assigned to this subject
          const assigned = arr
            .filter((row) => (row?.teacherUserId ?? row?.teacherId) != null && String(row?.subjectId) === sid)
            .map((row) => String(row?.teacherUserId ?? row?.teacherId));
          out[sid] = tid ? Array.from(new Set([tid, ...assigned])) : Array.from(new Set(assigned));
        } else {
          // Nursery: only the class teacher — no selection needed
          out[sid] = tid ? [tid] : [];
        }
      }
      return out;
    }
    const map = {};
    const arr = Array.isArray(teacherSubjects) ? teacherSubjects : [];
    for (const row of arr) {
      const sid = row?.subjectId;
      const tid = row?.teacherUserId ?? row?.teacherId;
      if (sid == null || tid == null) continue;
      const key = String(sid);
      if (!map[key]) map[key] = new Set();
      map[key].add(String(tid));
    }
    const out = {};
    for (const subject of subjects || []) {
      const key = String(subject?.id || '');
      if (!key) continue;
      const set = map[key];
      // ONLY THE TEACHERS ASSIGNED TO THIS SUBJECT.
      //
      // This used to fall back to `allTeacherIds` whenever a subject had no
      // assignments recorded, on the reasoning that not knowing who teaches
      // something is not the same as nobody doing. In a secondary school that
      // is the wrong trade: it offers the whole staff list for a subject and
      // invites picking someone who does not teach it, and the server then
      // refuses the save through assertTeacherSubjectAssignments — a rejection
      // for choosing an option the form itself presented.
      //
      // Empty is the honest answer, and the dialog already has the right
      // message for it. That message was unreachable until now, because the
      // fallback guaranteed a non-empty list every time.
      out[key] = set && set.size ? Array.from(set) : [];
    }
    return out;
  }, [isNurPriPhase, isNurPriPrimaryClass, nurPriClassTeacherId, subjects, teacherSubjects, teachers]);

  useEffect(() => {
    if (isNurPriPhase) return;
    const selected = Array.isArray(formData?.subjectIds)
      ? formData.subjectIds.map(String).filter((v) => v && v !== '__close__')
      : [];
    if (selected.length === 0) return;

    setFormData((prev) => {
      const prevMap = (prev && typeof prev.teacherBySubject === 'object' && prev.teacherBySubject) ? prev.teacherBySubject : {};
      let changed = false;
      const nextMap = { ...prevMap };

      for (const sid of selected) {
        // A BLANK SOMEBODY CHOSE IS NOT A BLANK TO FILL IN. Once "Not assigned
        // yet" became a real answer, this convenience turned into a trap: clear
        // the box on a subject with exactly one eligible teacher and it was
        // quietly put back, so a vacancy could never be left as one.
        if (teacherTouchedRef.current.has(String(sid))) continue;
        const current = String(nextMap[String(sid)] || '');
        const allowed = allowedTeacherIdsBySubjectId[String(sid)] || [];
        if (!current && allowed.length === 1) {
          nextMap[String(sid)] = `u:${allowed[0]}`;
          changed = true;
        }
      }

      return changed ? { ...prev, teacherBySubject: nextMap } : prev;
    });
  }, [isNurPriPhase, formData?.subjectIds, allowedTeacherIdsBySubjectId]);

  const getSlotDisplayText = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) return '';
    const activity = entries.find(e => (e?.activityTitle || '').trim());
    if (activity) {
      const title = String(activity.activityTitle || '').trim();
      if (title.toLowerCase() === String(FREE_PERIOD_ACTIVITY).toLowerCase()) {
        return t('schoolAdmin.timetableDesigner.activities.freePeriod');
      }
      return title;
    }

    const seen = new Set();
    const labels = [];
    for (const e of entries) {
      const label = (getSubjectName(e?.subjectId) || '').trim();
      if (!label) continue;
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      labels.push(label);
    }
    return labels.join(' / ');
  };
  const classOptions = useMemo(() => {
    if (!classes || !classes.length) return [];
    const { id: phaseIdFilter, code: phaseCodeFilter } = phaseContext;
    if (!phaseIdFilter && !phaseCodeFilter) return classes;
    const filtered = classes.filter(cls => {
      if (phaseIdFilter && cls.phaseId != null) {
        return Number(cls.phaseId) === Number(phaseIdFilter);
      }
      if (phaseCodeFilter && cls.phaseCode) {
        return String(cls.phaseCode).toLowerCase() === String(phaseCodeFilter).toLowerCase();
      }
      return true;
    });
    return filtered;
  }, [classes, phaseContext]);

  useEffect(() => {
    if (selectedClass === 'all') return;
    if (!classOptions || classOptions.length === 0) return;
    const exists = classOptions.some(c => String(c.id) === String(selectedClass));
    if (!exists) {
      setSelectedClass('all');
    }
  }, [classOptions, selectedClass]);

  // Filter subjects to only those assigned to the currently selected class
  const filteredSubjectsForClass = useMemo(() => {
    const classId = selectedClass !== 'all' ? String(selectedClass) : String(formData?.classId || '');
    if (!classId) return subjects || [];
    const assignedSubjectIds = new Set(
      (classSubjects || [])
        .filter(cs => String(cs?.classId) === classId && (cs?.isActive !== false))
        .map(cs => cs?.subjectId)
        .filter(Boolean)
        .map(String)
    );
    if (assignedSubjectIds.size === 0) return subjects || [];
    return (subjects || []).filter(s => assignedSubjectIds.has(String(s?.id)));
  }, [subjects, classSubjects, selectedClass, formData?.classId]);

  const phaseScopedClassEntries = useMemo(() => {
    if (selectedClass === 'all') return [];
    return workingEntries.filter((t) => String(t.classId) === String(selectedClass));
  }, [selectedClass, workingEntries]);

  /**
   * HOW MANY OF A CLASS'S PERIODS ARE STILL EMPTY — a sentence, not a padlock.
   *
   * This count used to be the reason the PDF button was disabled. But a
   * generated week is not finished by design: subjects the school has no teacher
   * for are placed as vacancies and whatever will not fit is left out, so the
   * one timetable anybody actually wanted on paper was the one it refused. Say
   * how many are open and hand over the file.
   *
   * Null in the teacher view on purpose. A teacher's empty period is a free
   * period, which is not a gap in anything.
   */
  const emptyPeriodCount = useMemo(() => {
    if (viewMode === 'teacher' || selectedClass === 'all') return null;
    let gaps = 0;
    for (const slot of periodSlots) {
      for (const day of computedDays) {
        const filled = phaseScopedClassEntries.some((entry) => (entry.dayOfWeek ?? entry.day) == day.value
          && normTime(entry.startTime) === normTime(slot.start));
        if (!filled) gaps += 1;
      }
    }
    return gaps;
  }, [viewMode, selectedClass, phaseScopedClassEntries, computedDays, periodSlots]);

  const metaReady = !!(
    (formData.timetableName || '').trim() &&
    selectedClass !== 'all'
  );

  // A timetable nobody named can be opened and printed, but not written back
  // until it is given one — the sentinel selects rows, it is not a name.
  const canSaveTimetable = !!(metaReady && selectedClass !== 'all' && !savingBatch
    && currentTimetableName !== UNNAMED);

  // EXPORTING NEEDS A SHEET TO EXPORT, AND NOTHING ELSE. Not a complete one:
  // `missingClassSlots.length === 0` used to be part of this, so a week with a
  // single free period could not be printed at all. Whether the timetable is
  // finished is the reader's business, and the gaps are on the page.
  const canExport = viewMode === 'teacher'
    ? !!(currentTimetableName && selectedTeacherKey && teacherEntries.length)
    : !!(currentTimetableName && selectedClass !== 'all');
  const hasUnsavedDraftEntries = !!draftDirty;

  const handleBulkSave = async () => {
    const entriesToSave = (draftEntries && draftEntries.length) ? draftEntries : persistedEntriesForCurrent;
    await persistTimetableEntries(entriesToSave);
  };

  const handleDeleteTimetable = async () => {
    try {
      if (!metaReady) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.metaRequiredDeleting'), 'warning');
        return;
      }
      if (!persistedEntriesForCurrent || persistedEntriesForCurrent.length === 0) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.noSavedTimetableToDelete'), 'info');
        return;
      }
      const name = (formData.timetableName || '').trim();
      if (!name) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.metaRequiredDeleting'), 'warning');
        return;
      }
      if ((formData.timetableType || 'class') === 'exam' && !formData.sequenceNumber) {
        showSnackbar(t('schoolAdmin.timetableDesigner.snack.selectExamSequenceBeforeDeleting'), 'warning');
        return;
      }
      const ok = window.confirm(t('schoolAdmin.timetableDesigner.snack.confirmDeleteTimetable', { name }));
      if (!ok) return;

      const qs = new URLSearchParams({
        classId: String(selectedClass),
        timetableType: String(formData.timetableType || 'class'),
        timetableName: name
      });
      if ((formData.timetableType || 'class') === 'exam' && formData.sequenceNumber) {
        qs.set('sequenceNumber', String(formData.sequenceNumber));
      }
      await fetchJson(`/api/academics/timetables/scope?${qs.toString()}`, { method: 'DELETE' });

      // Nothing to clear for the axis any more: it belongs to the class, not to
      // the timetable, and deleting a timetable does not change when the school
      // opens. The unsaved draft is another matter — that was about THIS
      // timetable and goes with it.
      try { if (draftStorageKey) localStorage.removeItem(draftStorageKey); } catch (_) {}
      setDraftEntries([]);
      setDraftDirty(false);
      setViewTimetableName('');
      setFormData((prev) => ({ ...prev, timetableName: '' }));
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.timetableDeletedSuccess'), 'success');
      fetchData();
    } catch (error) {
      console.error('Failed to delete timetable', error);
      showSnackbar(t('schoolAdmin.timetableDesigner.snack.deleteTimetableFail'), 'error');
    }
  };

  // Format times as English AM/PM for display
  const toAmPm = (hhmm) => {
    if (!hhmm) return '';
    const [h, m] = hhmm.split(':').map(Number);
    const am = h < 12;
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${m.toString().padStart(2,'0')} ${am ? t('schoolAdmin.timetableDesigner.time.am') : t('schoolAdmin.timetableDesigner.time.pm')}`;
  };
  const formatSlotLabel = (slot, compact = false) => {
    if (!slot) return '';
    const [start, end] = typeof slot === 'string'
      ? slot.split('-')
      : [slot.start || slot.startTime, slot.end || slot.endTime];

    // STACKED AND 24-HOUR ON A PHONE. "8:00 AM - 8:50 AM" is why the column was
    // 200px wide; the same information as two lines of "08:00" is 58px, and a
    // timetable is read as a sequence of periods where the meridiem is never in
    // doubt — school does not run through both eights.
    if (compact) {
      return (
        <>
          <div>{start}</div>
          <div>{end}</div>
        </>
      );
    }
    return `${toAmPm(start)} - ${toAmPm(end)}`;
  };

  /**
   * THE SHEET AS IT IS DRAWN — the same rows, the same columns, the same cells.
   *
   * Built from `gridSlots` and `computedDays`, which is exactly what the grid
   * above iterates, so the paper cannot drift from the screen: a teacher's sheet
   * comes out on the union of their classes' days, a class's on that class's own
   * day, and neither has to be described twice.
   */
  const buildSheet = () => {
    const isTeacher = viewMode === 'teacher';
    const isExam = (formData.timetableType || 'class') === 'exam';
    const who = isTeacher
      ? (teachersInTimetable.find((x) => x.key === selectedTeacherKey)?.name || '')
      : getClassName(selectedClass);

    const cellFor = (entries) => {
      if (!entries.length) return '';
      if (entries.some((e) => (e?.activityTitle || '').trim())) return getSlotDisplayText(entries);
      return entries.map((e) => {
        const subject = getSubjectName(e?.subjectId) || '';
        // The fact the other view carries in its header: which class on a
        // teacher's sheet, who is teaching on a class's.
        const under = isTeacher ? getClassName(e?.classId) : rowTeacherName(e);
        return under ? `${subject}\n${under}` : subject;
      }).join('\n');
    };

    const breakRows = new Set();

    const body = gridSlots.map((slot, rowIndex) => {
      const slotStart = slot.start || slot.startTime || (slot.startMinutes != null ? toHHMM(slot.startMinutes) : '');
      if (slot.type === 'break') {
        breakRows.add(rowIndex);
        return [formatSlotLabel(slot), ...computedDays.map(() => slot.label || '')];
      }
      const pool = isTeacher ? teacherEntries : workingEntries;
      return [formatSlotLabel(slot), ...computedDays.map((day) => {
        const entries = pool.filter((e) => {
          const classOk = isTeacher ? true : String(e.classId) === String(selectedClass);
          return classOk && (e.dayOfWeek ?? e.day) == day.value && normTime(e.startTime) === normTime(slotStart);
        });
        return cellFor(entries);
      })];
    });

    // The gaps are printed on the sheet, not a reason to refuse it.
    const subtitle = [
      sessionLabel || '',
      timetableLabel,
      emptyPeriodCount > 0
        ? t('schoolAdmin.timetableDesigner.pdf.emptyPeriods', {
          n: emptyPeriodCount,
          defaultValue: `${emptyPeriodCount} period(s) still empty`,
        })
        : '',
    ].filter(Boolean).join('  ·  ');

    const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'timetable';
    const seq = formData.sequenceNumber ? String(formData.sequenceNumber) : '';

    return {
      title: isExam
        ? t('schoolAdmin.timetableDesigner.pdf.examTitle', { who, defaultValue: `${who} — exam timetable` })
        : (isTeacher
          ? t('schoolAdmin.timetableDesigner.pdf.teacherTitle', { who, defaultValue: `${who} — timetable` })
          : t('schoolAdmin.timetableDesigner.pdf.title', { className: who })),
      subtitle: isExam && seq
        ? `${subtitle}  ·  ${t('schoolAdmin.timetableDesigner.labels.sequence', { defaultValue: 'Sequence' })} ${seq}`
        : subtitle,
      tables: [{
        head: [t('schoolAdmin.timetableDesigner.table.time'), ...computedDays.map((d) => d.label)],
        body,
        breakRows,
      }],
      filename: isExam
        ? `exam-timetable-${slug(who)}${seq ? `-sequence-${seq}` : ''}.pdf`
        : `timetable-${slug(who)}.pdf`,
    };
  };

  const handleExport = (printIt) => {
    if (!canExport) {
      showSnackbar(
        viewMode === 'teacher'
          ? t('schoolAdmin.timetableDesigner.snack.selectTeacherToDownload', { defaultValue: 'Choose a teacher first.' })
          : t('schoolAdmin.timetableDesigner.snack.selectClassToDownload'),
        'warning',
      );
      return;
    }
    const sheet = buildSheet();
    deliver(buildPdf(sheet), sheet.filename, printIt);
  };

  if (loading) return (
    <Box sx={{ p: 3 }}>
      <AppLoader
        variant="circular3"
        size={58}
        messages={[
          t('schoolAdmin.timetableDesigner.loading.loadingTimetables'),
          t('schoolAdmin.timetableDesigner.loading.loadingMetadata'),
          t('schoolAdmin.timetableDesigner.loading.applyingPhaseAy')
        ]}
      />
    </Box>
  );

  return (
    <div className="p-4 sm:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('schoolAdmin.timetableDesigner.pageTitle')}</h1>
      </div>
      <Dialog open={showForm} onClose={resetEntryForm}>
        <DialogTitle>{editingEntry ? t('schoolAdmin.timetableDesigner.dialog.editEntryTitle') : t('schoolAdmin.timetableDesigner.dialog.addEntryTitle')}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormControl fullWidth>
                <InputLabel id="class-label">{t('schoolAdmin.timetableDesigner.labels.class')}</InputLabel>
                <Select
                  labelId="class-label"
                  label={t('schoolAdmin.timetableDesigner.labels.class')}
                  value={effectiveFormClassId}
                  disabled={selectedClass !== 'all'}
                  onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                  required
                >
                  {classOptions.length === 0 && <MenuItem value="" disabled>{t('schoolAdmin.timetableDesigner.empty.noClassesForActivePhase')}</MenuItem>}
                  {classOptions.map(cls => (
                    <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth disabled={isFreePeriod}>
                <InputLabel id="subject-label">{t('schoolAdmin.timetableDesigner.labels.subject')}</InputLabel>
                <Select
                  labelId="subject-label"
                  label={t('schoolAdmin.timetableDesigner.labels.subject')}
                  multiple
                  open={subjectDropdownOpen}
                  onOpen={() => setSubjectDropdownOpen(true)}
                  onClose={() => setSubjectDropdownOpen(false)}
                  value={formData.subjectIds}
                  onChange={(e) => {
                    const nextRaw = e.target.value;
                    const next = Array.isArray(nextRaw) ? nextRaw : [];
                    if (next.includes('__close__')) {
                      const cleaned = next.filter((v) => v !== '__close__');
                      setFormData((prev) => {
                        const nextTeachers = { ...(prev.teacherBySubject || {}) };
                        for (const key of Object.keys(nextTeachers)) {
                          if (!cleaned.includes(String(key))) delete nextTeachers[key];
                        }
                        return { ...prev, subjectIds: cleaned, teacherBySubject: nextTeachers };
                      });
                      setSubjectDropdownOpen(false);
                      return;
                    }
                    setFormData((prev) => {
                      const nextTeachers = { ...(prev.teacherBySubject || {}) };
                      const normalized = (next || []).map(String).filter((v) => v && v !== '__close__');
                      for (const key of Object.keys(nextTeachers)) {
                        if (!normalized.includes(String(key))) delete nextTeachers[key];
                      }
                      return { ...prev, subjectIds: next, teacherBySubject: nextTeachers };
                    });
                  }}
                  renderValue={(selected) => {
                    const ids = Array.isArray(selected) ? selected : [];
                    return ids
                      .filter((v) => v && v !== '__close__')
                      .map((id) => getSubjectName(id))
                      .filter(Boolean)
                      .join(', ');
                  }}
                  required={!isFreePeriod}
                >
                  {filteredSubjectsForClass.map(subject => (
                    <MenuItem key={subject.id} value={String(subject.id)}>
                      <Checkbox checked={(formData.subjectIds || []).includes(String(subject.id))} />
                      {subject.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="__close__">{t('common.ok')}</MenuItem>
                </Select>
              </FormControl>

              {!isFreePeriod && (
                <div className="md:col-span-2">
                  {isNurPriPhase && !isNurPriPrimaryClass ? (
                    nurPriClassTeacherId ? (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('schoolAdmin.timetableDesigner.labels.autoAssignedTeacher', { defaultValue: 'Auto-assigned teacher (class teacher):' })}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {getTeacherName(nurPriClassTeacherId)}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="warning.dark">
                          {t('schoolAdmin.timetableDesigner.snack.assignClassTeacherBeforeNurPri', { defaultValue: 'Assign a class teacher to this class first.' })}
                        </Typography>
                      </Box>
                    )
                  ) : (
                    (formData.subjectIds || []).filter((v) => v && v !== '__close__').map((sid) => (
                      <FormControl key={`teacher-${sid}`} fullWidth sx={{ mt: 1 }}>
                        <InputLabel id={`teacher-label-${sid}`}>{t('schoolAdmin.timetableDesigner.labels.subjectTeacher', { subject: getSubjectName(sid) })}</InputLabel>
                        <Select
                          labelId={`teacher-label-${sid}`}
                          label={t('schoolAdmin.timetableDesigner.labels.subjectTeacher', { subject: getSubjectName(sid) })}
                          value={String((formData.teacherBySubject || {})[String(sid)] || '')}
                          onChange={(e) => {
                            const nextTeacherId = e.target.value;
                            teacherTouchedRef.current.add(String(sid));
                            setFormData((prev) => ({
                              ...prev,
                              teacherBySubject: {
                                ...(prev.teacherBySubject || {}),
                                [String(sid)]: String(nextTeacherId || '')
                              }
                            }));
                          }}
                        >
                          {/* NOBODY YET, said plainly. The blank option used to
                              read "Select a teacher" and saving without one was
                              refused, so a period the school knowingly has no
                              teacher for — 228 of them in the applied week —
                              could not be expressed or edited. */}
                          <MenuItem value="">
                            {t('schoolAdmin.timetableDesigner.labels.noTeacherYet', { defaultValue: 'Not assigned yet' })}
                          </MenuItem>
                          {(() => {
                            const allowedIds = allowedTeacherIdsBySubjectId[String(sid)] || [];
                            const allowedSet = new Set(allowedIds.map(String));
                            const filtered = (teachers || []).filter((t) => allowedSet.has(String(t.id)));
                            const hasFallback = !!(classTeacherFallbackOption && allowedSet.has(String(classTeacherFallbackOption.id)) && !filtered.some((t) => String(t.id) === String(classTeacherFallbackOption.id)));
                            // NAMED, NO ACCOUNT — offered for every subject,
                            // because that is what the school asked for when
                            // they moved placeholders into teacher settings:
                            // one name usable for all allocations rather than
                            // one per subject and level.
                            const named = Object.entries(placeholderNames || {})
                              .map(([id, name]) => ({ id, name }))
                              .sort((a, b) => String(a.name).localeCompare(String(b.name)));
                            if (filtered.length === 0 && !hasFallback && named.length === 0) {
                              return (
                                <MenuItem value="__none__" disabled>
                                  {t('schoolAdmin.timetableDesigner.empty.noTeachersAssignedToSubject', { defaultValue: 'No teachers assigned to this subject' })}
                                </MenuItem>
                              );
                            }
                            // WHICH ONE IS THE CLASS TEACHER, said on the option
                            // itself. In nursery and primary the class teacher
                            // takes most of the week, so they are the expected
                            // answer for nearly every slot — and in a list of
                            // plain names there was no way to tell which name
                            // that was without leaving the dialog to go and look.
                            const classTeacherLabel = t('schoolAdmin.timetableDesigner.labels.classTeacherSuffix', {
                              defaultValue: 'class teacher',
                            });
                            const nameFor = (row) => {
                              const base = row.name || row.email || String(row.id);
                              return isNurPriPhase && String(row.id) === String(nurPriClassTeacherId)
                                ? `${base} — ${classTeacherLabel}`
                                : base;
                            };
                            return [
                              ...filtered.map((row) => (
                                <MenuItem key={`u-${row.id}`} value={`u:${row.id}`}>{nameFor(row)}</MenuItem>
                              )),
                              ...(hasFallback ? [
                                <MenuItem key={`fallback-${classTeacherFallbackOption.id}`} value={`u:${classTeacherFallbackOption.id}`}>
                                  {nameFor(classTeacherFallbackOption)}
                                </MenuItem>
                              ] : []),
                              ...named.map((p) => (
                                <MenuItem key={`p-${p.id}`} value={`p:${p.id}`}>
                                  {p.name} — {t('schoolAdmin.timetableDesigner.labels.noAccountSuffix', { defaultValue: 'no account' })}
                                </MenuItem>
                              ))
                            ];
                          })()}
                        </Select>
                      </FormControl>
                    ))
                  )}
                </div>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFreePeriod}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setIsFreePeriod(next);
                      if (next) setFormData((prev) => ({ ...prev, subjectIds: [], teacherBySubject: {} }));
                    }}
                  />
                }
                label={t('schoolAdmin.timetableDesigner.labels.freePeriod')}
              />

              <FormControl fullWidth>
                <InputLabel id="day-label">{t('schoolAdmin.timetableDesigner.labels.dayOfWeek')}</InputLabel>
                <Select labelId="day-label" label={t('schoolAdmin.timetableDesigner.labels.dayOfWeek')} value={formData.dayOfWeek} onChange={(e) => setFormData({ ...formData, dayOfWeek: e.target.value })}>
                  {daysOfWeek.map(day => (
                    <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="start-label">{t('schoolAdmin.timetableDesigner.labels.startTime')}</InputLabel>
                <Select labelId="start-label" label={t('schoolAdmin.timetableDesigner.labels.startTime')} value={formData.startTime} onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}>
                  {computedSlots.map(slot => (
                    <MenuItem key={slot} value={slot.split('-')[0]}>
                      {toAmPm(slot.split('-')[0])}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="end-label">{t('schoolAdmin.timetableDesigner.labels.endTime')}</InputLabel>
                <Select labelId="end-label" label={t('schoolAdmin.timetableDesigner.labels.endTime')} value={formData.endTime} onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}>
                  {computedSlots.map(slot => (
                    <MenuItem key={slot} value={slot.split('-')[1]}>
                      {toAmPm(slot.split('-')[1])}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Timetable meta is configured on the main page now */}
            </div>

            {/* Conflict warnings */}
            {(() => {
              const ignoreKey = editingEntry?.id ?? editingEntry?.__localId ?? null;
              const issues = detectConflictsFor(formData, workingEntries, ignoreKey);
              return issues.length ? (
                <div className="space-y-2">
                  <Alert severity="warning">{t('schoolAdmin.timetableDesigner.conflicts.detectedOverrideToSave')}</Alert>
                  <ul className="list-disc pl-5 text-sm">
                    {issues.slice(0,3).map((m, i) => (<li key={i}>{m}</li>))}
                  </ul>
                  {(() => {
                    const sug = proposeSlots(formData);
                    return sug.length ? (
                      <div className="text-sm">
                        <div className="font-medium mb-1">{t('schoolAdmin.timetableDesigner.conflicts.suggestedSlots')}</div>
                        <div className="flex flex-wrap gap-2">
                          {sug.map((s, idx) => (
                            <Button key={idx} size="small" variant="outlined" onClick={() => setFormData({ ...formData, dayOfWeek: String(daysOfWeek.find(d=>d.label===s.dayLabel).value), startTime: s.slot.split('-')[0], endTime: s.slot.split('-')[1] })}>
                              {s.dayLabel} • {s.slot}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={overrideConflict} onChange={(e) => setOverrideConflict(e.target.checked)} /> {t('schoolAdmin.timetableDesigner.conflicts.override')}
                  </label>
                </div>
              ) : null;
            })()}

            <DialogActions>
              <Button type="button" onClick={resetEntryForm}>{t('common.cancel')}</Button>
              <Button type="submit" variant="contained">{editingEntry ? t('schoolAdmin.timetableDesigner.actions.updateEntry') : t('schoolAdmin.timetableDesigner.actions.createEntry')}</Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
      <Card>
        <CardHeader title={t('schoolAdmin.timetableDesigner.builder.title')} subheader={t('schoolAdmin.timetableDesigner.builder.subheader')} />
        <CardContent>
          {/* Timetable meta configuration */}
          <div className="flex flex-wrap items-end gap-4 mb-3 p-3 rounded" style={{ ...grid.timeCell, border: grid.border }}>
            <TextField
              label={t('schoolAdmin.timetableDesigner.labels.academicSession')}
              size="small"
              value={sessionLabel || t('schoolAdmin.timetableDesigner.session.currentAcademicYear')}
              InputProps={{ readOnly: true }}
              helperText={t('schoolAdmin.timetableDesigner.session.helperText')}
              sx={{ minWidth: 220 }}
            />
            <TextField
              label={t('schoolAdmin.timetableDesigner.labels.timetableName')}
              size="small"
              // Empty, not the sentinel: it is a way of selecting the rows
              // nobody named, and typing here is how they get a name.
              value={formData.timetableName === UNNAMED ? '' : formData.timetableName}
              placeholder={formData.timetableName === UNNAMED
                ? t('schoolAdmin.timetableDesigner.labels.unnamedTimetable', { defaultValue: '(unnamed)' })
                : undefined}
              helperText={formData.timetableName === UNNAMED
                ? t('schoolAdmin.timetableDesigner.helperText.nameBeforeSaving', { defaultValue: 'Give it a name before saving' })
                : undefined}
              onChange={(e) => setFormData((prev) => ({ ...prev, timetableName: e.target.value }))}
              sx={{ minWidth: 240 }}
            />

            <FormControl sx={{ minWidth: 240 }} size="small">
              <InputLabel id="view-tt-name">{t('schoolAdmin.timetableDesigner.labels.viewTimetable')}</InputLabel>
              <Select
                labelId="view-tt-name"
                label={t('schoolAdmin.timetableDesigner.labels.viewTimetable')}
                value={viewTimetableName}
                onChange={(e) => {
                  const next = e.target.value;
                  setViewTimetableName(next);
                  loadTimetableByName(next);
                }}
              >
                <MenuItem value="">{t('schoolAdmin.timetableDesigner.labels.selectTimetable')}</MenuItem>
                {timetableNameOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="timetable-type-label">{t('schoolAdmin.timetableDesigner.labels.timetableType')}</InputLabel>
              <Select
                labelId="timetable-type-label"
                label={t('schoolAdmin.timetableDesigner.labels.timetableType')}
                value={formData.timetableType}
                onChange={(e) => {
                  const next = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    timetableType: next,
                    sequenceNumber: next === 'exam' ? prev.sequenceNumber : ''
                  }));
                }}
              >
                <MenuItem value="class">{t('schoolAdmin.timetableDesigner.timetableTypes.class')}</MenuItem>
                <MenuItem value="exam">{t('schoolAdmin.timetableDesigner.timetableTypes.exam')}</MenuItem>
              </Select>
            </FormControl>

            {/* BY CLASS OR BY TEACHER — the same timetable, two ways of reading
                it. A teacher's week is not a third kind of timetable stored
                somewhere else; it is the rows they teach, gathered from every
                class. It was the one view the page never offered, and it is the
                one a staffroom asks for. */}
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="timetable-view-label">
                {t('schoolAdmin.timetableDesigner.labels.view', { defaultValue: 'View' })}
              </InputLabel>
              <Select
                labelId="timetable-view-label"
                label={t('schoolAdmin.timetableDesigner.labels.view', { defaultValue: 'View' })}
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
              >
                <MenuItem value="class">
                  {t('schoolAdmin.timetableDesigner.views.byClass', { defaultValue: 'By class' })}
                </MenuItem>
                <MenuItem value="teacher">
                  {t('schoolAdmin.timetableDesigner.views.byTeacher', { defaultValue: 'By teacher' })}
                </MenuItem>
              </Select>
            </FormControl>

            {viewMode === 'teacher' && (
              <FormControl sx={{ minWidth: 240 }} size="small">
                <InputLabel id="timetable-teacher-label">
                  {t('schoolAdmin.timetableDesigner.labels.teacher', { defaultValue: 'Teacher' })}
                </InputLabel>
                <Select
                  labelId="timetable-teacher-label"
                  label={t('schoolAdmin.timetableDesigner.labels.teacher', { defaultValue: 'Teacher' })}
                  value={selectedTeacherKey}
                  onChange={(e) => setSelectedTeacherKey(e.target.value)}
                >
                  {teachersInTimetable.map((x) => (
                    <MenuItem key={x.key} value={x.key}>
                      {x.name} · {x.periods} periods
                    </MenuItem>
                  ))}
                  {!teachersInTimetable.length && (
                    <MenuItem value="" disabled>
                      {t('schoolAdmin.timetableDesigner.labels.noTeachersInTimetable', { defaultValue: 'Nobody is teaching in this timetable yet' })}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
            {formData.timetableType === 'exam' && (
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="exam-seq-label">{t('schoolAdmin.timetableDesigner.labels.examSequence')}</InputLabel>
                <Select
                  labelId="exam-seq-label"
                  label={t('schoolAdmin.timetableDesigner.labels.examSequence')}
                  value={formData.sequenceNumber || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, sequenceNumber: e.target.value }))}
                >
                  <MenuItem value="1">{t('schoolAdmin.timetableDesigner.examSequences.1')}</MenuItem>
                  <MenuItem value="2">{t('schoolAdmin.timetableDesigner.examSequences.2')}</MenuItem>
                  <MenuItem value="3">{t('schoolAdmin.timetableDesigner.examSequences.3')}</MenuItem>
                  <MenuItem value="4">{t('schoolAdmin.timetableDesigner.examSequences.4')}</MenuItem>
                  <MenuItem value="5">{t('schoolAdmin.timetableDesigner.examSequences.5')}</MenuItem>
                  <MenuItem value="6">{t('schoolAdmin.timetableDesigner.examSequences.6')}</MenuItem>
                </Select>
              </FormControl>
            )}
          </div>

          {/* Builder controls: axes, working class/teacher + breaks */}
          <div className="flex flex-wrap items-end gap-4 mb-4">
            {/* Timetable axes */}
            <TextField label={t('schoolAdmin.timetableDesigner.labels.periodMinutes')} type="number" size="small" value={periodMinutes} onChange={(e) => setPeriodMinutes(Math.max(5, Number(e.target.value)||0))} sx={{ width: 160 }} />
            <TextField label={t('schoolAdmin.timetableDesigner.labels.dayStart')} type="time" size="small" value={dayStart} onChange={(e) => setDayStart(e.target.value)} sx={{ width: 150 }} inputProps={{ step: 300 }} />
            <TextField label={t('schoolAdmin.timetableDesigner.labels.dayEnd')} type="time" size="small" value={dayEnd} onChange={(e) => setDayEnd(e.target.value)} sx={{ width: 150 }} inputProps={{ step: 300 }} />
            <FormControl sx={{ minWidth: 140 }} size="small">
              <InputLabel id="week-start">{t('schoolAdmin.timetableDesigner.labels.weekStart')}</InputLabel>
              <Select labelId="week-start" label={t('schoolAdmin.timetableDesigner.labels.weekStart')} value={weekStart} onChange={(e) => setWeekStart(e.target.value)}>
                {daysOfWeek.map(d => (<MenuItem key={'s'+d.value} value={d.value}>{d.label}</MenuItem>))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 140 }} size="small">
              <InputLabel id="week-end">{t('schoolAdmin.timetableDesigner.labels.weekEnd')}</InputLabel>
              <Select labelId="week-end" label={t('schoolAdmin.timetableDesigner.labels.weekEnd')} value={weekEnd} onChange={(e) => setWeekEnd(e.target.value)}>
                {daysOfWeek.map(d => (<MenuItem key={'e'+d.value} value={d.value}>{d.label}</MenuItem>))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="builder-class">{t('schoolAdmin.timetableDesigner.labels.workingClass')}</InputLabel>
              <Select labelId="builder-class" label={t('schoolAdmin.timetableDesigner.labels.workingClass')} value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                <MenuItem value="all">{t('schoolAdmin.timetableDesigner.labels.selectAClass')}</MenuItem>
                {classOptions.map(c => (<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>))}
              </Select>
            </FormControl>
            <TextField
              label={t('schoolAdmin.timetableDesigner.labels.shortBreakAfterPeriods')}
              type="number"
              size="small"
              value={shortBreakConfig.periodsBefore}
              onChange={(e) => {
                const next = Math.max(1, parseInt(e.target.value, 10) || 1);
                setShortBreakConfig((prev) => ({ ...prev, periodsBefore: next }));
              }}
              sx={{ width: 220 }}
              inputProps={{ min: 1 }}
              helperText={t('schoolAdmin.timetableDesigner.helperText.breakInsertsAfterPeriods')}
            />
            <TextField
              label={t('schoolAdmin.timetableDesigner.labels.shortBreakDurationMinutes')}
              type="number"
              size="small"
              value={shortBreakConfig.durationMinutes}
              onChange={(e) => {
                const next = Math.max(5, parseInt(e.target.value, 10) || 5);
                setShortBreakConfig((prev) => ({ ...prev, durationMinutes: next }));
              }}
              sx={{ width: 220 }}
              inputProps={{ min: 5, step: 5 }}
            />
            <TextField
              label={t('schoolAdmin.timetableDesigner.labels.longBreakAfterPeriods')}
              type="number"
              size="small"
              value={longBreakConfig.periodsBefore}
              onChange={(e) => {
                const next = Math.max(1, parseInt(e.target.value, 10) || 1);
                setLongBreakConfig((prev) => ({ ...prev, periodsBefore: next }));
              }}
              sx={{ width: 220 }}
              inputProps={{ min: 1 }}
            />
            <TextField
              label={t('schoolAdmin.timetableDesigner.labels.longBreakDurationMinutes')}
              type="number"
              size="small"
              value={longBreakConfig.durationMinutes}
              onChange={(e) => {
                const next = Math.max(5, parseInt(e.target.value, 10) || 5);
                setLongBreakConfig((prev) => ({ ...prev, durationMinutes: next }));
              }}
              sx={{ width: 220 }}
              inputProps={{ min: 5, step: 5 }}
            />
            <Button variant="outlined" onClick={() => handleExport(false)} disabled={!canExport}>
              {t('schoolAdmin.timetableDesigner.actions.downloadPdf')}
            </Button>
            <Button variant="outlined" onClick={() => handleExport(true)} disabled={!canExport}>
              {t('schoolAdmin.timetableDesigner.actions.print', { defaultValue: 'Print' })}
            </Button>
            {/* Said, rather than enforced. This used to be a disabled button and
                no explanation of what was missing. */}
            {emptyPeriodCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {t('schoolAdmin.timetableDesigner.pdf.emptyPeriods', {
                  n: emptyPeriodCount,
                  defaultValue: `${emptyPeriodCount} period(s) still empty`,
                })}
              </Typography>
            )}
            <Button variant="contained" onClick={handleBulkSave} disabled={!canSaveTimetable}>
              {savingBatch ? t('schoolAdmin.timetableDesigner.actions.saving') : t('schoolAdmin.timetableDesigner.actions.saveTimetable')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteTimetable}
              disabled={!metaReady || savingBatch || persistedEntriesForCurrent.length === 0}
            >
              {t('schoolAdmin.timetableDesigner.actions.deleteTimetable')}
            </Button>
          </div>
          <div className="overflow-x-auto">
            {!metaReady && (
              <Alert severity="info" className="mb-3">{t('schoolAdmin.timetableDesigner.snack.metaRequiredAddEntries')}</Alert>
            )}
            {metaReady && hasUnsavedDraftEntries && (
              <Alert severity="warning" className="mb-3">
                You have unsaved timetable draft changes. These slots are visible only in this draft and will not be available for attendance until you click Save Timetable.
              </Alert>
            )}
            {/* SAID, NOT SWALLOWED. Rows with no phase cannot be drawn here —
                the page is scoped to one — but a timetable that is on file and
                invisible looks exactly like no timetable at all, which is how
                276 applied rows once disappeared without a word. */}
            {rowsWithoutPhase > 0 && (
              <Alert severity="warning" className="mb-3">
                {t('schoolAdmin.timetableDesigner.alerts.rowsWithoutPhase', {
                  n: rowsWithoutPhase,
                  defaultValue: `${rowsWithoutPhase} timetable row(s) this year carry no section and cannot be shown here. They were saved before the section was recorded — regenerate or re-apply that timetable to bring them back.`,
                })}
              </Alert>
            )}
            {academicYearLoading && (
              <Alert severity="info" className="mb-3">{t('schoolAdmin.timetableDesigner.loading.loadingAcademicYears')}</Alert>
            )}
            {academicYearError && (
              <Alert severity="error" className="mb-3">{t('schoolAdmin.timetableDesigner.errors.loadAcademicYears')}</Alert>
            )}
            <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
              {/* 58px is enough for two stacked 24-hour times at 10px, which is
                  what the time cell renders on a phone. The day columns come
                  down with it so more than one is on screen at a time — the
                  whole point of taking the width back. */}
              <colgroup>
                <col style={{ width: isPhone ? 58 : 200 }} />
                {computedDays.map((day) => (
                  <col key={`col-${day.value}`} style={{ width: isPhone ? 124 : 220 }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className="p-2 text-center sticky left-0 z-30 shadow-sm" style={{ ...grid.head, border: grid.border }}>{t('schoolAdmin.timetableDesigner.table.time')}</th>
                  {computedDays.map(day => (
                    <th key={day.value} className="p-2 text-center sticky top-0 z-20" style={{ ...grid.head, border: grid.border }}>{day.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gridSlots.map((slot, idx) => {
                  const slotStart = slot.start || slot.startTime || (slot.startMinutes != null ? toHHMM(slot.startMinutes) : '');
                  const slotEnd = slot.end || slot.endTime || (slot.endMinutes != null ? toHHMM(slot.endMinutes) : '');
                  const isBreakSlot = slot.type === 'break';
                  const rowKey = `${slot.type}-${slotStart}-${slotEnd}-${idx}`;
                  return (
                    <tr key={rowKey}>
                      <td
                        // px-1, NOT p-1. `p-1` cut the vertical padding too and
                        // every row lost 8px of height — the grid got shorter
                        // when the only thing that needed to shrink was its
                        // width. Horizontal padding narrows the column; vertical
                        // padding is what makes a slot big enough to tap.
                        className={`font-medium text-center sticky left-0 z-10 shadow-inner ${isPhone ? 'px-1 py-2' : 'p-2 whitespace-nowrap'}`}
                        style={{
                          ...(isBreakSlot ? grid.breakCell : grid.timeCell),
                          border: grid.border,
                          ...(isPhone ? { fontSize: 10, lineHeight: 1.25 } : null),
                        }}
                      >
                        {formatSlotLabel(slot, isPhone)}
                      </td>
                      {computedDays.map(day => {
                        if (isBreakSlot) {
                          return (
                            <td
                              key={`${rowKey}-${day.value}`}
                              className="text-center text-sm font-semibold"
                              style={{ ...grid.breakCell, border: grid.border }}
                            >
                              {slot.label}
                            </td>
                          );
                        }
                        // BY CLASS, OR BY TEACHER ACROSS CLASSES. A teacher's
                        // week is the rows they teach wherever they teach them,
                        // so the class filter is what drops out — not the day or
                        // the time, which mean the same thing either way.
                        const pool = viewMode === 'teacher' ? teacherEntries : workingEntries;
                        const entries = pool.filter(t => {
                          const classOk = viewMode === 'teacher'
                            ? true
                            : (selectedClass !== 'all' ? String(t.classId) === String(selectedClass) : true);
                          return classOk && (t.dayOfWeek ?? t.day) == day.value && normTime(t.startTime) === normTime(slotStart);
                        });
                        const hasConflict = entries.some(e => detectConflictsFor(e, pool, e.id ?? e.__localId).length > 0);
                        const isPublished = entries.some(e => e.status === 'published');
                        const cellTone = hasConflict ? grid.conflictCell
                          : (isPublished ? grid.publishedCell : grid.emptyCell);
                        const slotText = getSlotDisplayText(entries);

                        return (
                          <td
                            key={`${rowKey}-${day.value}`}
                            // p-1 on a phone: at 124px a column, 8px of padding
                            // each side is a sixth of the cell spent on nothing.
                            // Horizontal only, for the same reason. The day
                            // cell is what sets the row height — it carries the
                            // subject and the teacher — so trimming its vertical
                            // padding shortened the whole grid.
                            className={`align-top break-words whitespace-normal text-sm leading-snug ${isPhone ? 'px-1 py-2' : 'p-2'}`}
                            style={{ ...cellTone, border: grid.border }}
                            onClick={() => {
                              // A TEACHER'S SLOT BELONGS TO A CLASS, and that is
                              // the class being edited. The row lives in that
                              // class's week and the save is per class, so the
                              // page switches to it rather than pretending a
                              // teacher has a timetable of their own to write.
                              //
                              // A free hour in a teacher's week belongs to no
                              // class, so there is nothing to add to it — adding
                              // is done from the class view, where the question
                              // "which class?" has already been answered.
                              if (viewMode === 'teacher') {
                                if (!entries.length) {
                                  showSnackbar(t('schoolAdmin.timetableDesigner.snack.addFromClassView', { defaultValue: 'A free period belongs to no class. Switch to By class to add a lesson.' }), 'info');
                                  return;
                                }
                                const owners = [...new Set(entries.map((e) => String(e.classId)))];
                                if (owners.length > 1) {
                                  showSnackbar(t('schoolAdmin.timetableDesigner.snack.twoClassesAtOnce', { defaultValue: 'This period has lessons in more than one class — open it from By class to say which one you mean.' }), 'warning');
                                  return;
                                }
                                setSelectedClass(owners[0]);
                                setFormData((prev) => ({ ...prev, classId: owners[0] }));
                                handleEditSlot(entries, slotStart, slotEnd, day.value);
                                return;
                              }
                              if (!metaReady) { showSnackbar(t('schoolAdmin.timetableDesigner.snack.metaRequiredAddEntries'), 'warning'); return; }
                              if (selectedClass === 'all') { showSnackbar(t('schoolAdmin.timetableDesigner.snack.selectClassBeforeFillingSlots'), 'warning'); return; }
                              ensureDraftInitialized();
                              if (entries.length) {
                                handleEditSlot(entries, slotStart, slotEnd, day.value);
                                return;
                              }
                              setEditingEntry(null);
                              setIsFreePeriod(false);
                              setFormData((prev) => ({
                                ...prev,
                                classId: selectedClass !== 'all' ? selectedClass : prev.classId,
                                dayOfWeek: day.value,
                                startTime: normTime(slotStart),
                                endTime: normTime(slotEnd)
                              }));
                              setShowForm(true);
                            }}
                          >
                            <div className="flex flex-col gap-2">
                              {!!slotText && (
                                <div className="space-y-1">
                                  {(entries || []).some(e => (e?.activityTitle || '').trim()) ? (
                                    <div className="text-sm font-semibold" style={grid.strong}>
                                      {slotText}
                                    </div>
                                  ) : (
                                    (entries || []).filter(e => !e?.activityTitle).map((e, i) => (
                                      <div key={e?.id ?? e?.__localId ?? `${e?.subjectId}-${i}`}>
                                        <div className="text-sm font-semibold" style={grid.strong}>
                                          {getSubjectName(e?.subjectId)}
                                        </div>
                                        <div className="text-[10px]" style={grid.muted}>
                                          {/* WHICH CLASS on a teacher's sheet, WHO on a
                                              class's — each view shows the fact the
                                              other one carries in its header. */}
                                          {viewMode === 'teacher'
                                            ? (getClassName(e?.classId) || '')
                                            : rowTeacherName(e)}
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                              {!slotText && (
                                <div className="text-[11px] italic" style={grid.muted}>{t('schoolAdmin.timetableDesigner.builder.clickToAdd')}</div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};
export default TimetableDesignerPage;
